import React, { useState, useEffect } from 'react';
import { useWindowSize } from '../hooks/useWindowSize';
import {
  Table,
  Button,
  Select,
  DatePicker,
  Input,
  Tag,
  Space,
  Card,
  Row,
  Col,
  Statistic,
  Modal,
  message,
  Tooltip,
  Badge
} from 'antd';
import {
  FiSearch,
  FiFilter,
  FiEye,
  FiCheckCircle,
  FiXCircle,
  FiShoppingBag,
  FiCalendar,
  FiMapPin,
  FiRefreshCw,
  FiDownload,
  FiPhone,
  FiClipboard,
  FiSettings,
  FiFileText,
  FiFile,
  FiPaperclip,
  FiList,
  FiGrid,
  FiUser,
  FiClock
} from 'react-icons/fi';
import { FaChevronLeft, FaChevronRight } from 'react-icons/fa';
import dayjs from 'dayjs';
import { API_ENDPOINTS } from '../utils/config';
import AdminService from '../services/adminService';
import '../css/admin/BusinessReviewModal.css';

const { Option } = Select;
const { RangePicker } = DatePicker;

const PendingBusinessDashboard = () => {
  const { width, height, isMobile, isTablet, isDesktop, isSmallMobile } = useWindowSize();

  const BASE = `${API_ENDPOINTS.BASE_URL}/business`;
  const getAuthHeaders = () => {
    try {
      const token = localStorage.getItem('admin_token');
      return token ? { Authorization: `Bearer ${token}` } : {};
    } catch (_) {
      return {};
    }
  };
  const getAdminId = () => {
    try {
      return (
        localStorage.getItem('admin_id') ||
        localStorage.getItem('admin_user_id') ||
        localStorage.getItem('user_id') ||
        null
      );
    } catch (_) {
      return null;
    }
  };

  const formatPaymentModes = (pm, upiVpa = null, isCodAvailable = null) => {
    const list = [];
    if (pm && pm !== '—') {
      if (typeof pm === 'string') return pm;
      if (typeof pm === 'object') {
        if (pm.upi) list.push('UPI');
        if (pm.card) list.push('Card');
        if (pm.cash) list.push('Cash');
        if (pm.net_banking) list.push('Net Banking');
      }
    }
    if (list.length === 0) {
      if (upiVpa) list.push('UPI');
      if (isCodAvailable === true) list.push('Cash on Delivery (COD)');
    }
    return list.length ? list.join(', ') : '—';
  };

  const formatDeliveryPreferences = (dp, orderTypes = []) => {
    if (dp && dp !== '—') {
      if (typeof dp === 'string') return dp;
      if (typeof dp === 'object') {
        const list = [];
        if (dp.pickup_only) list.push('Pickup Only');
        if (dp.self_delivery) list.push('Self Delivery');
        if (dp.kirazee_delivery) list.push('Kirazee Delivery');
        if (list.length) return list.join(', ');
      }
    }
    if (Array.isArray(orderTypes) && orderTypes.length > 0) {
      return orderTypes.map(t => {
        const typeStr = String(t).toLowerCase();
        if (typeStr === 'dine_in') return 'Dine-in';
        if (typeStr === 'pickup') return 'Pickup';
        if (typeStr === 'delivery') return 'Delivery';
        return typeStr.replace(/_/g, ' ').replace(/\b\w/g, m => m.toUpperCase());
      }).join(', ');
    }
    return '—';
  };

  const formatWorkingHours = (wh) => {
    if (!wh || wh === '—') return '—';
    if (typeof wh === 'string') return wh;
    try {
      // Check legacy single-range object format
      if (wh.opening_time || wh.open || wh.start || wh.closing_time || wh.close || wh.end) {
        const open = wh.opening_time || wh.open || wh.start;
        const close = wh.closing_time || wh.close || wh.end;
        const tz = wh.timezone || wh.tz;
        const range = (open || close) ? `${open || ''}${open && close ? ' - ' : ''}${close || ''}` : '';
        const tzStr = tz ? ` (${tz})` : '';
        return (range || tzStr) ? `${range}${tzStr}` : '—';
      }

      // Check day-by-day object format (e.g., { mon: [...], tue: [...] })
      const daysOrder = ['mon', 'tue', 'wed', 'thu', 'fri', 'sat', 'sun'];
      const dayNames = {
        mon: 'Mon', tue: 'Tue', wed: 'Wed', thu: 'Thu', fri: 'Fri', sat: 'Sat', sun: 'Sun'
      };

      const dayKeys = Object.keys(wh).filter(k =>
        daysOrder.includes(k.toLowerCase()) ||
        ['monday', 'tuesday', 'wednesday', 'thursday', 'friday', 'saturday', 'sunday'].includes(k.toLowerCase())
      );

      if (dayKeys.length > 0) {
        const getDayStr = (val) => {
          if (!val) return 'Closed';
          if (Array.isArray(val)) {
            if (val.length === 0) return 'Closed';
            return val.map(slot => (slot.open && slot.close ? `${slot.open} - ${slot.close}` : (slot.open || slot.close || 'Closed'))).join(', ');
          }
          if (typeof val === 'object') {
            if (val.is_closed || val.closed) return 'Closed';
            if (val.open && val.close) return `${val.open} - ${val.close}`;
          }
          if (typeof val === 'string') return val;
          return 'Closed';
        };

        const normalized = {};
        daysOrder.forEach(d => {
          const matchingKey = Object.keys(wh).find(k => k.toLowerCase() === d || k.toLowerCase().startsWith(d));
          normalized[d] = matchingKey ? getDayStr(wh[matchingKey]) : null;
        });

        // Check if all active days have the exact same schedule
        const activeDays = daysOrder.filter(d => normalized[d] !== null);
        if (activeDays.length > 0) {
          const firstVal = normalized[activeDays[0]];
          const allSame = activeDays.every(d => normalized[d] === firstVal);
          if (allSame) {
            if (activeDays.length === 7) {
              return `Mon - Sun: ${firstVal}`;
            }
            return `${activeDays.map(d => dayNames[d]).join(', ')}: ${firstVal}`;
          }

          // Check if weekdays (Mon-Fri) are the same and weekends (Sat-Sun) are the same
          const weekdays = ['mon', 'tue', 'wed', 'thu', 'fri'].filter(d => normalized[d] !== null);
          const weekends = ['sat', 'sun'].filter(d => normalized[d] !== null);
          const weekdaysSame = weekdays.length > 0 && weekdays.every(d => normalized[d] === normalized[weekdays[0]]);
          const weekendsSame = weekends.length > 0 && weekends.every(d => normalized[d] === normalized[weekends[0]]);

          if (weekdaysSame && weekendsSame) {
            return `Mon - Fri: ${normalized[weekdays[0]]} | Sat - Sun: ${normalized[weekends[0]]}`;
          }

          return activeDays.map(d => `${dayNames[d]}: ${normalized[d]}`).join(' | ');
        }
      }

      return '—';
    } catch (_) {
      return '—';
    }
  };

  const formatFileSize = (bytes) => {
    if (!bytes || isNaN(bytes)) return '';
    if (bytes < 1024) return `${bytes} B`;
    if (bytes < 1024 * 1024) return `${(bytes / 1024).toFixed(1)} KB`;
    return `${(bytes / (1024 * 1024)).toFixed(1)} MB`;
  };

  const getDocumentTypeLabel = (type) => {
    if (!type) return 'Document';
    const map = {
      fssai_license: 'FSSAI License',
      business_registration: 'Business Registration',
      bank_proof: 'Bank Proof / Cheque',
      identity_proof: 'Identity Proof',
      pan_card: 'PAN Card',
      store_photo: 'Store Photo',
      gst_certificate: 'GST Certificate',
      trade_license: 'Trade License',
      address_proof: 'Address Proof'
    };
    return map[type] || String(type).replace(/_/g, ' ').replace(/\b\w/g, (c) => c.toUpperCase());
  };
  const [businesses, setBusinesses] = useState([]);
  const [filteredBusinesses, setFilteredBusinesses] = useState([]);
  const [apiStatistics, setApiStatistics] = useState(null);
  const [loading, setLoading] = useState(false);
  const [selectedBusiness, setSelectedBusiness] = useState(null);
  const [detailModalVisible, setDetailModalVisible] = useState(false);
  const [selectedRowKeys, setSelectedRowKeys] = useState([]);
  const [bulkActionLoading, setBulkActionLoading] = useState(false);
  const [verifyingDocId, setVerifyingDocId] = useState(null);
  const [documentModalVisible, setDocumentModalVisible] = useState(false);
  const [selectedDocument, setSelectedDocument] = useState(null);
  const [decisionModalVisible, setDecisionModalVisible] = useState(false);
  const [decisionAction, setDecisionAction] = useState(null);
  const [decisionBusinessId, setDecisionBusinessId] = useState(null);
  const [decisionComments, setDecisionComments] = useState('');
  const [decisionRejectionReasons, setDecisionRejectionReasons] = useState([]);
  const [decisionRequiredChanges, setDecisionRequiredChanges] = useState([]);

  // Review Templates Management
  const [reviewTemplates, setReviewTemplates] = useState({
    rejection: [],
    required_changes: [],
    approval: []
  });
  const [templatesLoading, setTemplatesLoading] = useState(false);

  // Pagination states
  const [currentPage, setCurrentPage] = useState(1);
  const [pageSize, setPageSize] = useState(12);
  const [totalPages, setTotalPages] = useState(1);
  const [totalItems, setTotalItems] = useState(0);

  // Filter states
  const [filters, setFilters] = useState({
    status: 'all',
    category: 'all',
    dateRange: null,
    searchText: ''
  });

  // View mode state
  const [viewMode, setViewMode] = useState('grid'); // 'list' or 'grid' - default to grid
  const [categories, setCategories] = useState([]);

  // Load review templates and business categories when component mounts
  useEffect(() => {
    loadReviewTemplates();
    fetchCategories();
  }, []);

  const fetchCategories = async () => {
    try {
      const response = await AdminService.getBusinessTypes();
      let types = [];
      if (Array.isArray(response)) {
        types = response;
      } else if (response && response.data && Array.isArray(response.data)) {
        types = response.data;
      } else if (response && response.businessTypes && Array.isArray(response.businessTypes)) {
        types = response.businessTypes;
      } else if (response) {
        for (const key in response) {
          if (Array.isArray(response[key])) {
            types = response[key];
            break;
          }
        }
      }
      setCategories(types);
    } catch (err) {
      console.error('Failed to fetch business types for categories dropdown:', err);
    }
  };

  const loadReviewTemplates = async () => {
    try {
      setTemplatesLoading(true);
      const response = await AdminService.getReviewTemplates();
      if (response.success) {
        const templates = response.data || [];
        const grouped = {
          rejection: templates.filter(t => t.reason_type === 'rejection'),
          required_changes: templates.filter(t => t.reason_type === 'required_changes'),
          approval: templates.filter(t => t.reason_type === 'approval')
        };
        setReviewTemplates(grouped);
      }
    } catch (error) {
      console.error('Failed to load review templates:', error);
    } finally {
      setTemplatesLoading(false);
    }
  };

  const mapApplicationToRow = (app) => {
    const applicationId = app?.application_id || app?.id || app?.business_id;
    const businessInfo = app?.business_info || app?.business || {};
    const financialDetails = businessInfo?.financial_details || app?.financial_details || {};
    const ownerInfo = app?.owner_info || app?.owner || {};
    const address = businessInfo?.address || app?.address || {};
    const operationalDetails = app?.operational_details || {};
    const documents = app?.documents || businessInfo?.documents || [];
    const city = address?.city || '';
    const state = address?.state || '';
    const fullAddress = address?.full_address || address?.formatted_address || '';
    const location = app?.location || fullAddress || [city, state].filter(Boolean).join(', ');
    const categoryRaw = businessInfo?.business_category || businessInfo?.category || businessInfo?.business_type || app?.category || 'others';
    const businessTypeRaw = businessInfo?.business_type || app?.business_type || categoryRaw;
    const statusRaw = app?.status || app?.business_status || 'pending';
    const rejectionReasons = app?.rejection_reasons || app?.status?.rejection_reasons || [];
    const requiredChanges = app?.required_changes || app?.status?.required_changes || [];
    const adminComments = app?.comments || app?.status?.comments || '';

    let submissionDate = app?.submitted_at || app?.submission_date || app?.created_at || null;
    if (!submissionDate && Array.isArray(app?.submission_timeline) && app.submission_timeline.length > 0) {
      submissionDate = app.submission_timeline[app.submission_timeline.length - 1]?.timestamp || app.submission_timeline[0]?.timestamp;
    }

    const toUiStatus = (s) => {
      const val = String(s || '').toLowerCase();
      if (val === 'approved') return 'Approved';
      if (val === 'rejected') return 'Rejected';
      if (val === 'in_progress') return 'In Progress';
      if (val === 'requires_changes' || val === 'request_changes' || val === 'changes_requested') return 'Requires Changes';
      if (val === 'submitted') return 'Submitted';
      if (val === 'pending' || val === 'pending review' || val === 'under_review' || val === 'under review') return 'Pending Review';
      return val ? val.replace(/_/g, ' ').replace(/\b\w/g, (m) => m.toUpperCase()) : 'Pending Review';
    };

    return {
      id: applicationId,
      application_id: applicationId,
      applicationId: applicationId || '—',
      businessName: app?.business_name || businessInfo?.business_name || businessInfo?.name || 'Unknown',
      category: typeof categoryRaw === 'string' ? categoryRaw.toLowerCase() : 'others',
      businessType: typeof businessTypeRaw === 'string' ? businessTypeRaw.replace(/_/g, ' ').replace(/\b\w/g, (m) => m.toUpperCase()) : 'Others',
      ownerName: ownerInfo?.name || app?.owner_name || 'Unknown',
      location: location || '—',
      businessAddress: fullAddress || [address?.street, address?.district !== city ? address?.district : null, city, state, address?.pincode || address?.zipcode, address?.country].filter(Boolean).join(', ') || location || '—',
      submissionDate: submissionDate,
      status: toUiStatus(statusRaw),
      rawStatus: String(statusRaw || '').toLowerCase(),
      phone: ownerInfo?.phone || app?.phone || businessInfo?.phone || '—',
      email: ownerInfo?.email || app?.email || businessInfo?.email || '—',
      registrationNumber: app?.registration_number || businessInfo?.registration_number || '—',
      gstin: financialDetails?.gstin || businessInfo?.gstin || app?.gstin || '—',
      bankAccountNumber: financialDetails?.account_number || businessInfo?.account_number || businessInfo?.bank_account_number || app?.bank_account_number || app?.account_number || '—',
      accountHolderName: financialDetails?.account_holder_name || businessInfo?.account_holder_name || ownerInfo?.name || '—',
      ifscCode: financialDetails?.ifsc_code || businessInfo?.ifsc_code || app?.ifsc_code || '—',
      ownerPan: financialDetails?.owner_pan || businessInfo?.owner_pan || ownerInfo?.pan || ownerInfo?.pan_number || app?.owner_pan || app?.pan_number || '—',
      fssaiNumber: businessInfo?.fssai_number || app?.fssai_number || '—',
      storeDescription: businessInfo?.store_description || app?.store_description || '',
      workingHours: app?.working_hours || operationalDetails?.working_hours || businessInfo?.working_hours || '—',
      paymentModes: app?.payment_modes || operationalDetails?.payment_modes || '—',
      deliveryPreferences: app?.delivery_preferences || operationalDetails?.delivery_preferences || '—',
      orderTypes: operationalDetails?.order_types || app?.order_types || [],
      isCodAvailable: operationalDetails?.is_cod_available ?? app?.is_cod_available ?? null,
      upiVpa: operationalDetails?.upi_vpa || app?.upi_vpa || '',
      businessWhatsapp: operationalDetails?.business_whatsapp || operationalDetails?.businessWhatsapp || app?.business_whatsapp || app?.businessWhatsapp || '',
      submissionTimeline: app?.submission_timeline || [],
      documents: Array.isArray(documents) ? documents.map((d) => {
        if (typeof d === 'string') return { type: 'Document', name: d, url: d, file_url: d };
        return {
          id: d?.document_id || d?.id,
          document_id: d?.document_id || d?.id,
          type: d?.document_type || d?.type || 'Document',
          document_type: d?.document_type || d?.type || 'Document',
          name: d?.file_name || d?.name || d?.document_type || d?.type || 'Document',
          file_name: d?.file_name || d?.name || '',
          url: d?.file_url || d?.url || d?.upload_url || '',
          file_url: d?.file_url || d?.url || d?.upload_url || '',
          content_type: d?.content_type || d?.mime_type || '',
          file_size: d?.file_size,
          uploaded_at: d?.uploaded_at,
          verification_status: d?.verification_status || 'pending'
        };
      }) : [],
      rejection_reasons: Array.isArray(rejectionReasons) ? rejectionReasons : [],
      required_changes: Array.isArray(requiredChanges) ? requiredChanges : [],
      comments: adminComments
    };
  };

  const openDocument = (doc) => {
    const url = doc?.url || doc?.upload_url || doc?.file_url || (typeof doc === 'string' ? doc : '');
    if (!url) {
      message.warning('Document URL not available');
      return;
    }

    // Open all documents in new tab
    window.open(url, '_blank', 'noopener,noreferrer');
  };

  const getFileExtension = (url) => {
    if (!url) return '';
    const urlWithoutQuery = url.split('?')[0];
    const match = urlWithoutQuery.match(/\.([^.]+)$/);
    return match ? match[1].toLowerCase() : '';
  };

  const isSameOrigin = (url) => {
    try {
      const urlObj = new URL(url);
      return urlObj.origin === window.location.origin;
    } catch (e) {
      return false;
    }
  };

  const isImageFile = (url, contentType) => {
    if (contentType && contentType.startsWith('image/')) return true;
    const ext = getFileExtension(url);
    return ['png', 'jpg', 'jpeg', 'gif', 'bmp', 'webp', 'svg'].includes(ext);
  };

  const isPdfFile = (url, contentType) => {
    if (contentType && contentType === 'application/pdf') return true;
    const ext = getFileExtension(url);
    return ext === 'pdf';
  };

  const isDocumentFile = (url, contentType) => {
    if (contentType && (contentType.includes('word') || contentType.includes('document') || contentType.includes('msword') || contentType.includes('openxml'))) return true;
    const ext = getFileExtension(url);
    return ['doc', 'docx', 'xls', 'xlsx', 'ppt', 'pptx'].includes(ext);
  };

  const getDocumentViewerUrl = (url) => {
    const ext = getFileExtension(url);

    // For same-origin URLs, check if we can use external viewers
    if (!isSameOrigin(url)) {
      const encodedUrl = encodeURIComponent(url);

      // Use Microsoft Office Online Viewer for Office documents
      if (['doc', 'docx', 'xls', 'xlsx', 'ppt', 'pptx'].includes(ext)) {
        return `https://view.officeapps.live.com/op/view.aspx?src=${encodedUrl}`;
      }

      // Use Google Docs Viewer as fallback
      return `https://docs.google.com/viewer?url=${encodedUrl}&embedded=true`;
    }

    // For same-origin URLs that might be blocked by X-Frame-Options, return null to force direct display
    return null;
  };

  useEffect(() => {
    const fetchData = async () => {
      await loadBusinesses();
    };
    fetchData();
  }, [filters, currentPage]); // Add currentPage to dependency array

  const loadBusinesses = async () => {
    setLoading(true);
    try {
      // Build query parameters
      const params = new URLSearchParams({
        page: currentPage,
        limit: pageSize,
        ...(filters.searchText && { search: filters.searchText }),
        ...(filters.category && filters.category !== 'all' && { category: filters.category }),
        ...(filters.dateRange?.[0] && filters.dateRange?.[1] && {
          start_date: dayjs(filters.dateRange[0]).format('YYYY-MM-DD'),
          end_date: dayjs(filters.dateRange[1]).format('YYYY-MM-DD')
        })
      });

      // Determine the correct endpoint based on status filter
      let endpoint = '/onboarding/admin/business-applications/';
      switch (filters.status) {
        case 'approved':
          endpoint += 'approved';
          break;
        case 'rejected':
          endpoint += 'rejected';
          break;
        case 'pending':
          endpoint += 'pending';
          break;
        case 'in_progress':
          endpoint = '/onboarding/in_progress/business-applications/pending';
          break;
        default:
          endpoint += 'all';
      }

      const response = await fetch(`${BASE}${endpoint}?${params.toString()}`, {
        headers: {
          'Content-Type': 'application/json',
          ...getAuthHeaders()
        }
      });

      if (!response.ok) {
        const errorData = await response.json();
        throw new Error(errorData.detail || 'Failed to fetch businesses');
      }

      const data = await response.json();
      if (data.success && data.data && data.data.applications) {
        const mapped = data.data.applications.map(mapApplicationToRow);
        setBusinesses(mapped);
        setFilteredBusinesses(mapped);

        // Capture API statistics if available
        if (data.data.statistics) {
          setApiStatistics(data.data.statistics);
        }

        // Update pagination if available in response
        if (data.data.pagination) {
          const p = data.data.pagination;
          setCurrentPage(p.current_page || 1);
          setPageSize(12);
          if (typeof p.total_items === 'number') {
            setTotalItems(p.total_items);
            setTotalPages(Math.ceil(p.total_items / 12) || 1);
          } else if (p.total_pages) {
            setTotalPages(p.total_pages);
          }
        }
      } else {
        console.error('Unexpected response format:', data);
        message.error('Failed to load business applications: Invalid response format');
      }
    } catch (error) {
      console.error('Error loading businesses:', error);
      message.error(`Failed to load business applications: ${error.message || 'Unknown error'}`);
    } finally {
      setLoading(false);
    }
  };

  // Pagination functions
  const paginatedBusinesses = filteredBusinesses;

  const handlePageChange = (page) => {
    if (page >= 1 && page <= totalPages) {
      setCurrentPage(page);
    }
  };

  const handleFilterChange = (key, value) => {
    setFilters(prev => ({
      ...prev,
      [key]: value
    }));
  };

  const clearFilters = () => {
    setFilters({
      status: 'all',
      category: 'all',
      location: '',
      dateRange: null,
      searchText: ''
    });
  };


  const viewBusinessDetails = async (business) => {
    // Show modal with existing row data immediately
    setSelectedBusiness(business);
    setDetailModalVisible(true);
    try {
      const id = business?.application_id || business?.id;
      const response = await fetch(`${BASE}/onboarding/admin/business-applications/${id}/details`, { headers: { 'Content-Type': 'application/json', ...getAuthHeaders() } });
      if (!response.ok) {
        throw new Error('Failed to fetch details');
      }
      const json = await response.json();
      const d = json?.data || json;

      const detailMapped = mapApplicationToRow({
        ...business,
        ...d,
        application_id: d?.application_id || id,
        business_name: d?.business_name || business?.businessName,
        business_info: d?.business_info || business?.business_info || {},
        owner_info: d?.owner_info || business?.owner_info,
        documents: d?.documents || business?.documents,
        submission_date: d?.submitted_at || d?.last_updated || d?.submission_date || business?.submissionDate,
        status: d?.status || d?.business_status || business?.status,
        operational_details: d?.operational_details || business?.operational_details,
        working_hours: d?.working_hours || d?.operational_details?.working_hours || business?.workingHours,
        payment_modes: d?.payment_modes || d?.operational_details?.payment_modes,
        delivery_preferences: d?.delivery_preferences || d?.operational_details?.delivery_preferences,
        order_types: d?.order_types || d?.operational_details?.order_types,
        is_cod_available: d?.is_cod_available ?? d?.operational_details?.is_cod_available,
        upi_vpa: d?.upi_vpa || d?.operational_details?.upi_vpa,
        rejection_reasons: d?.status?.rejection_reasons || d?.rejection_reasons,
        required_changes: d?.status?.required_changes || d?.required_changes,
        comments: d?.status?.comments || d?.comments,
        submission_timeline: d?.submission_timeline || business?.submissionTimeline
      });

      // Preserve the original status if it was approved (don't let API overwrite it)
      if (business?.status === 'Approved' || business?.rawStatus === 'approved') {
        detailMapped.status = business.status;
        detailMapped.rawStatus = business.rawStatus;
      }

      setSelectedBusiness((prev) => ({ ...(prev || {}), ...detailMapped }));
    } catch (e) {
      message.error('Failed to load business details');
      setSelectedBusiness(business);
    }
  };

  const handleBusinessAction = async (businessId, action, extra = {}) => {
    if (!businessId) {
      message.error('Invalid application id');
      return;
    }
    const newStatus = action === 'approve' ? 'Approved' : action === 'pending' ? 'Requires Changes' : 'Rejected';
    try {
      const id = businessId;
      const payloadAction = action === 'approve' ? 'approve' : (action === 'pending' ? 'request_changes' : 'reject');
      const payload = { action: payloadAction };
      const adminId = getAdminId();
      if (adminId) payload.admin_id = adminId;
      if (extra && typeof extra.comments === 'string' && extra.comments.trim()) payload.comments = extra.comments.trim();
      if (payloadAction === 'reject' && Array.isArray(extra.rejection_reasons) && extra.rejection_reasons.length) {
        payload.rejection_reasons = extra.rejection_reasons;
      }
      if (payloadAction === 'request_changes' && Array.isArray(extra.required_changes) && extra.required_changes.length) {
        payload.required_changes = extra.required_changes;
      }
      const response = await fetch(`${BASE}/onboarding/admin/business-applications/${id}/review`, {
        method: 'POST',
        headers: { 'Content-Type': 'application/json', ...getAuthHeaders() },
        body: JSON.stringify(payload)
      });
      if (!response.ok) throw new Error('Review failed');
      let respJson = {};
      try { respJson = await response.json(); } catch (_) { respJson = {}; }
      const respData = respJson?.data || respJson || {};
      const updatedRaw = payloadAction === 'approve' ? 'approved' : payloadAction === 'reject' ? 'rejected' : 'requires_changes';
      const rejectionReasons = respData.rejection_reasons || respData.status?.rejection_reasons || extra.rejection_reasons || [];
      const requiredChanges = respData.required_changes || respData.status?.required_changes || extra.required_changes || [];
      const comments = respData.comments || respData.status?.comments || extra.comments || '';
      
      if (action === 'approve') {
        setSelectedBusiness(prev => {
          if (!prev) return prev;
          if (prev.id === id || prev.application_id === id) {
            return {
              ...prev,
              status: newStatus,
              rawStatus: updatedRaw,
              documents: (prev.documents || []).map(d => ({
                ...d,
                verification_status: 'approved'
              }))
            };
          }
          return prev;
        });
      }

      setBusinesses(prevBusinesses =>
        prevBusinesses.map(business =>
          business.id === id || business.application_id === id
            ? {
              ...business,
              status: newStatus,
              rawStatus: updatedRaw,
              documents: action === 'approve'
                ? (business.documents || []).map(d => ({ ...d, verification_status: 'approved' }))
                : business.documents,
              rejection_reasons: Array.isArray(rejectionReasons) ? rejectionReasons : [],
              required_changes: Array.isArray(requiredChanges) ? requiredChanges : [],
              comments: typeof comments === 'string' ? comments : '',
              actionDate: new Date().toISOString().split('T')[0]
            }
            : business
        )
      );
      if (action === 'approve') {
        message.success({ content: `Business approved successfully! All documents approved.`, style: { marginTop: '100px' }, duration: 3 });
      } else if (action === 'pending') {
        message.info({ content: `Requested changes recorded.`, style: { marginTop: '100px' }, duration: 3 });
      } else {
        message.warning({ content: `Business rejected successfully!`, style: { marginTop: '100px' }, duration: 3 });
      }
    } catch (error) {
      message.error('Failed to update business status');
    }
  };

  const handleVerifyDocument = async (doc, targetStatus = 'approved') => {
    const applicationId = selectedBusiness?.application_id || selectedBusiness?.id;
    const documentId = doc?.document_id || doc?.id || doc?.document_type || doc?.type;
    if (!applicationId || !documentId) {
      message.error('Missing application ID or document identifier');
      return;
    }
    setVerifyingDocId(documentId);
    try {
      let success = false;
      let errorMsg = '';
      try {
        const res = await fetch(`${BASE}/onboarding/admin/business-applications/${applicationId}/documents/${documentId}/verify`, {
          method: 'POST',
          headers: { 'Content-Type': 'application/json', ...getAuthHeaders() },
          body: JSON.stringify({ verification_status: targetStatus })
        });
        if (res.ok) {
          success = true;
        } else {
          const errData = await res.json().catch(() => ({}));
          errorMsg = errData?.error || errData?.message || '';
        }
      } catch (err) {
        errorMsg = err?.message || '';
      }

      if (!success) {
        try {
          const docType = doc?.document_type || doc?.type || documentId;
          await AdminService.verifyBusinessDocument(applicationId, docType, targetStatus);
          success = true;
        } catch (fbErr) {
          console.warn('Fallback also failed:', fbErr);
        }
      }

      if (success) {
        message.success(
          targetStatus === 'approved'
            ? 'Document approved successfully!'
            : targetStatus === 'rejected'
            ? 'Document marked as rejected'
            : 'Document verification reverted to pending'
        );

        setSelectedBusiness(prev => {
          if (!prev) return prev;
          const updatedDocs = (prev.documents || []).map(d => {
            const isMatch = (d.document_id && d.document_id === documentId) ||
                            (d.id && d.id === documentId) ||
                            (d.document_type && d.document_type === documentId) ||
                            (d.type && d.type === documentId);
            return isMatch ? { ...d, verification_status: targetStatus } : d;
          });
          return { ...prev, documents: updatedDocs };
        });

        setBusinesses(prev => prev.map(b => {
          if (b.id === applicationId || b.application_id === applicationId) {
            const updatedDocs = (b.documents || []).map(d => {
              const isMatch = (d.document_id && d.document_id === documentId) ||
                              (d.id && d.id === documentId) ||
                              (d.document_type && d.document_type === documentId) ||
                              (d.type && d.type === documentId);
              return isMatch ? { ...d, verification_status: targetStatus } : d;
            });
            return { ...b, documents: updatedDocs };
          }
          return b;
        }));
      } else {
        message.error(errorMsg || 'Failed to update document verification status');
      }
    } catch (e) {
      message.error(e?.message || 'Error verifying document');
    } finally {
      setVerifyingDocId(null);
    }
  };

  const openDecision = (record, action) => {
    const id = record?.application_id || record?.id;
    setDecisionAction(action);
    setDecisionBusinessId(id);
    setDecisionComments('');
    setDecisionRejectionReasons([]);
    setDecisionRequiredChanges([]);
    setDecisionModalVisible(true);
    setDetailModalVisible(false);
  };

  const submitDecision = async () => {
    if (decisionAction === 'reject') {
      if (!decisionComments.trim() && decisionRejectionReasons.length === 0) {
        message.warning('Please provide at least one rejection reason or comment.');
        return;
      }
    } else if (decisionAction === 'pending') {
      if (!decisionComments.trim() && decisionRequiredChanges.length === 0) {
        message.warning('Please provide at least one required change or comment.');
        return;
      }
    }

    await handleBusinessAction(decisionBusinessId, decisionAction, {
      comments: decisionComments,
      rejection_reasons: decisionRejectionReasons,
      required_changes: decisionRequiredChanges
    });
    setDecisionModalVisible(false);
  };

  const handleBulkAction = async (action) => {
    if (selectedRowKeys.length === 0) {
      message.warning('Please select businesses to perform bulk action');
      return;
    }
    setBulkActionLoading(true);
    try {
      for (const id of selectedRowKeys) {
        const payload = action === 'approve' ? { action: 'approve' } : { action: 'reject' };
        const res = await fetch(`${BASE}/onboarding/admin/business-applications/${id}/review`, {
          method: 'POST',
          headers: { 'Content-Type': 'application/json', ...getAuthHeaders() },
          body: JSON.stringify(payload)
        });
        if (!res.ok) throw new Error('Bulk review failed');
      }
      const newStatus = action === 'approve' ? 'Approved' : 'Rejected';
      setBusinesses(prev => prev.map(b =>
        selectedRowKeys.includes(b.id)
          ? {
              ...b,
              status: newStatus,
              documents: action === 'approve'
                ? (b.documents || []).map(d => ({ ...d, verification_status: 'approved' }))
                : b.documents,
              actionDate: new Date().toISOString().split('T')[0]
            }
          : b
      ));
      const count = selectedRowKeys.length;
      setSelectedRowKeys([]);
      message.success({
        content: `${count} businesses ${action === 'approve' ? 'approved' : 'rejected'} successfully!`,
        style: { marginTop: '100px' },
        duration: 3
      });
    } catch (error) {
      message.error('Failed to perform bulk action');
    } finally {
      setBulkActionLoading(false);
    }
  };

  const rowSelection = {
    selectedRowKeys,
    onChange: (keys) => setSelectedRowKeys(keys),
  };

  // Function to generate statistics cards dynamically based on API data
  const getStatisticsCards = () => {
    // Use API statistics if available, otherwise fall back to calculated statistics
    if (apiStatistics) {
      const stats = [];

      // Add total applications
      if (apiStatistics.total !== undefined) {
        stats.push({
          title: 'Total Applications',
          value: apiStatistics.total,
          color: '#2A2C41',
          borderColor: '#2A2C41',
          shadowColor: 'rgba(42, 44, 65, 0.1)'
        });
      }

      // Add food businesses (show even if 0)
      if (apiStatistics.food_businesses !== undefined) {
        stats.push({
          title: 'Food Businesses',
          value: apiStatistics.food_businesses,
          color: '#F55D00',
          borderColor: '#F55D00',
          shadowColor: 'rgba(245, 93, 0, 0.1)'
        });
      }

      // Add grocery stores (show even if 0)
      if (apiStatistics.grocery_stores !== undefined) {
        stats.push({
          title: 'Grocery Stores',
          value: apiStatistics.grocery_stores,
          color: '#FDBF50',
          borderColor: '#FDBF50',
          shadowColor: 'rgba(253, 191, 80, 0.1)'
        });
      }

      // Add other categories
      if (apiStatistics.other_categories !== undefined) {
        stats.push({
          title: 'Other Categories',
          value: apiStatistics.other_categories,
          color: '#2A2C41',
          borderColor: '#2A2C41',
          shadowColor: 'rgba(42, 44, 65, 0.1)'
        });
      }

      return stats;
    }

    // Fallback to calculated statistics if API stats not available
    if (!filteredBusinesses || filteredBusinesses.length === 0) {
      return [];
    }

    const stats = [
      {
        title: 'Total Applications',
        value: filteredBusinesses.length,
        color: '#2A2C41',
        borderColor: '#2A2C41',
        shadowColor: 'rgba(42, 44, 65, 0.1)'
      }
    ];

    // Get unique categories from the data
    const categories = [...new Set(filteredBusinesses.map(b => b.category))];

    // Add category-specific cards dynamically
    categories.forEach(category => {
      const categoryCount = filteredBusinesses.filter(b => b.category === category).length;
      if (categoryCount > 0) {
        const colors = getCategoryColors(category);
        stats.push({
          title: formatCategoryTitle(category),
          value: categoryCount,
          color: colors.color,
          borderColor: colors.borderColor,
          shadowColor: colors.shadowColor
        });
      }
    });

    return stats;
  };

  // Fallback function to calculate statistics from filtered data
  const getCalculatedStatistics = () => {
    if (!filteredBusinesses || filteredBusinesses.length === 0) {
      return [];
    }

    const stats = [
      {
        title: 'Total Applications',
        value: filteredBusinesses.length,
        color: '#2A2C41',
        borderColor: '#2A2C41',
        shadowColor: 'rgba(42, 44, 65, 0.1)'
      }
    ];

    // Get unique categories from the data
    const categories = [...new Set(filteredBusinesses.map(b => b.category))];

    // Add category-specific cards dynamically
    categories.forEach(category => {
      const categoryCount = filteredBusinesses.filter(b => b.category === category).length;
      if (categoryCount > 0) {
        const colors = getCategoryColors(category);
        stats.push({
          title: formatCategoryTitle(category),
          value: categoryCount,
          color: colors.color,
          borderColor: colors.borderColor,
          shadowColor: colors.shadowColor
        });
      }
    });

    return stats;
  };

  // Helper function to format category titles
  const formatCategoryTitle = (category) => {
    if (!category) return 'Unknown';

    // Convert category code to readable format
    const categoryMap = {
      'food': 'Food Businesses',
      'grocery': 'Grocery Stores',
      'clothing': 'Clothing & Fashion',
      'others': 'Other Categories',
      'Retail_and_Wholesale': 'Retail & Wholesale',
      'Food_and_Beverage': 'Food & Beverage',
      'Fashion_Retail': 'Fashion Retail',
      'Pharmacy_and_Healthcare': 'Pharmacy & Healthcare'
    };

    return categoryMap[category] || category.charAt(0).toUpperCase() + category.slice(1);
  };

  // Helper function to get colors for categories
  const getCategoryColors = (category) => {
    const colorSchemes = {
      'food': { color: '#F55D00', borderColor: '#F55D00', shadowColor: 'rgba(245, 93, 0, 0.1)' },
      'grocery': { color: '#FDBF50', borderColor: '#FDBF50', shadowColor: 'rgba(253, 191, 80, 0.1)' },
      'clothing': { color: '#8B5CF6', borderColor: '#8B5CF6', shadowColor: 'rgba(139, 92, 246, 0.1)' },
      'others': { color: '#2A2C41', borderColor: '#2A2C41', shadowColor: 'rgba(42, 44, 65, 0.1)' },
      'Retail_and_Wholesale': { color: '#10B981', borderColor: '#10B981', shadowColor: 'rgba(16, 185, 129, 0.1)' },
      'Food_and_Beverage': { color: '#F59E0B', borderColor: '#F59E0B', shadowColor: 'rgba(245, 158, 11, 0.1)' },
      'Fashion_Retail': { color: '#EC4899', borderColor: '#EC4899', shadowColor: 'rgba(236, 72, 153, 0.1)' },
      'Pharmacy_and_Healthcare': { color: '#06B6D4', borderColor: '#06B6D4', shadowColor: 'rgba(6, 182, 212, 0.1)' }
    };

    return colorSchemes[category] || { color: '#2A2C41', borderColor: '#2A2C41', shadowColor: 'rgba(42, 44, 65, 0.1)' };
  };

  const exportToCSV = () => {
    const csvData = filteredBusinesses.map(business => ({
      'Business Name': business.businessName,
      'Owner Name': business.ownerName,
      'Business Type': business.businessType || getCategoryLabel(business.category),
      'Location': business.location,
      'Phone': business.phone,
      'Email': business.email,
      'Registration Number': business.registrationNumber,
      'Submission Date': business.submissionDate ? dayjs(business.submissionDate).format('YYYY-MM-DD') : '',
      'Status': business.status,
      'Working Hours': business.workingHours,
    }));

    const csvContent = [
      Object.keys(csvData[0]).join(','),
      ...csvData.map(row => Object.values(row).map(value => `"${value}"`).join(','))
    ].join('\n');

    const blob = new Blob([csvContent], { type: 'text/csv;charset=utf-8;' });
    const link = document.createElement('a');
    const url = URL.createObjectURL(blob);
    link.setAttribute('href', url);
    link.setAttribute('download', `pending-businesses-${dayjs().format('YYYY-MM-DD')}.csv`);
    link.style.visibility = 'hidden';
    document.body.appendChild(link);
    link.click();
    document.body.removeChild(link);

    message.success('Business data exported successfully!');
  };

  const getCategoryColor = (category) => {
    const colors = {
      food: '#F55D00',
      grocery: '#FDBF50',
      clothing: '#2A2C41',
      others: '#F55D00'
    };
    return colors[category] || '#DE7E07';
  };

  const getCategoryLabel = (category) => {
    const labels = {
      food: 'Food',
      grocery: 'Grocery',
      clothing: 'Clothing',
      others: 'Others'
    };
    return labels[category] || category;
  };


  const columns = [
    {
      title: 'Business Name',
      dataIndex: 'businessName',
      key: 'businessName',
      render: (text, record) => (
        <div>
          <div style={{ fontWeight: '600', color: '#2A2C41', fontSize: '15px' }}>
            {text}
          </div>
          <div style={{ fontSize: '13px', color: '#2A2C41' }}>
            {record.ownerName}
          </div>
        </div>
      ),
    },
    {
      title: 'Business Type',
      dataIndex: 'category',
      key: 'category',
      render: (category, record) => (
        <Tag
          color={getCategoryColor(category)}
          style={{
            borderRadius: '6px',
            fontWeight: '500',
            fontSize: '12px'
          }}
        >
          {record.businessType || getCategoryLabel(category)}
        </Tag>
      ),
    },
    {
      title: 'Location',
      dataIndex: 'location',
      key: 'location',
      render: (location) => (
        <div style={{ display: 'flex', alignItems: 'center', gap: '6px', minWidth: 0 }}>
          <FiMapPin style={{ color: '#2A2C41', fontSize: '12px', flex: '0 0 auto' }} />
          <span style={{
            fontSize: '13px',
            color: '#2A2C41',
            overflow: 'hidden',
            textOverflow: 'ellipsis',
            whiteSpace: 'nowrap',
            display: 'inline-block',
            maxWidth: '220px'
          }}>{location}</span>
        </div>
      ),
    },
    {
      title: 'Submission Date',
      dataIndex: 'submissionDate',
      key: 'submissionDate',
      render: (date) => (
        <div style={{ display: 'flex', alignItems: 'center', gap: '6px' }}>
          <FiCalendar style={{ color: '#2A2C41', fontSize: '12px' }} />
          <span style={{ fontSize: '13px', color: '#2A2C41' }}>
            {date ? dayjs(date).format('MMM DD, YYYY') : '—'}
          </span>
        </div>
      ),
    },
    {
      title: 'Status',
      dataIndex: 'status',
      key: 'status',
      render: (status) => {
        let badgeStatus = 'processing';
        let textColor = '#2A2C41';
        let backgroundColor = 'rgba(42, 44, 65, 0.1)';

        if (status === 'Approved') {
          badgeStatus = 'success';
          textColor = '#FDBF50';
          backgroundColor = 'rgba(253, 191, 80, 0.1)';
        } else if (status === 'Rejected') {
          badgeStatus = 'error';
          textColor = '#F55D00';
          backgroundColor = 'rgba(245, 93, 0, 0.1)';
        }

        return (
          <div style={{
            padding: '4px 8px',
            borderRadius: '6px',
            backgroundColor: backgroundColor,
            display: 'inline-flex',
            alignItems: 'center',
            gap: '6px'
          }}>
            <Badge status={badgeStatus} />
            <span style={{
              color: textColor,
              fontWeight: '500',
              fontSize: '13px'
            }}>
              {status}
            </span>
          </div>
        );
      },
    },
    {
      title: 'Notes',
      key: 'notes',
      render: (_, record) => {
        const isRejected = record.status === 'Rejected';
        const isReqChanges = record.status === 'Requires Changes';
        const items = isRejected ? (record.rejection_reasons || []) : (isReqChanges ? (record.required_changes || []) : []);
        if (!items || items.length === 0) return null;
        return (
          <div style={{ display: 'flex', gap: '6px', flexWrap: 'wrap', maxWidth: 260 }}>
            {items.slice(0, 3).map((t, idx) => (
              <Tag key={idx} color={isRejected ? '#F55D00' : '#FDBF50'} style={{ borderRadius: '6px' }}>
                {String(t)}
              </Tag>
            ))}
            {items.length > 3 && (
              <Tag style={{ borderRadius: '6px' }}>+{items.length - 3}</Tag>
            )}
          </div>
        );
      }
    },
    {
      title: 'Actions',
      key: 'actions',
      render: (_, record) => {
        const raw = String(record.rawStatus || '').toLowerCase();
        const statusGroup = (
          raw === 'approved' ? 'approved' :
            raw === 'rejected' ? 'rejected' :
              (raw === 'pending' || raw === 'submitted' || raw === 'under_review' || raw === 'under review') ? 'pending' :
                (raw === 'in_progress' || raw === 'requires_changes' || raw === 'request_changes') ? 'in-progress' :
                  'pending'
        );
        const isRequiresChanges = raw === 'requires_changes' || raw === 'request_changes' || raw === 'changes_requested';
        const showApprove = statusGroup === 'pending' || statusGroup === 'rejected' || isRequiresChanges;
        const showReject = statusGroup === 'pending' || statusGroup === 'in-progress' || isRequiresChanges || statusGroup === 'approved';
        const showPending = statusGroup === 'in-progress' || statusGroup === 'rejected' || isRequiresChanges;
        return (
          <Space size="small">
            <Tooltip title="View Details">
              <Button
                type="text"
                icon={<FiEye />}
                onClick={() => viewBusinessDetails(record)}
                style={{
                  color: '#2A2C41',
                  borderRadius: '8px',
                  padding: '4px 8px',
                  height: '32px'
                }}
              >
                View
              </Button>
            </Tooltip>
            {showApprove && (
              <Tooltip title="Approve Business">
                <Button
                  size="small"
                  icon={<FiCheckCircle />}
                  onClick={() => openDecision(record, 'approve')}
                  style={{
                    color: '#ffffff',
                    backgroundColor: '#FDBF50',
                    borderColor: '#FDBF50',
                    borderRadius: '8px',
                    fontWeight: '500',
                    height: '32px',
                    padding: '4px 12px'
                  }}
                  onMouseEnter={(e) => {
                    e.currentTarget.style.backgroundColor = '#F55D00';
                    e.currentTarget.style.borderColor = '#F55D00';
                  }}
                  onMouseLeave={(e) => {
                    e.currentTarget.style.backgroundColor = '#FDBF50';
                    e.currentTarget.style.borderColor = '#FDBF50';
                  }}
                >
                  Approve
                </Button>
              </Tooltip>
            )}
            {showReject && (
              <Tooltip title="Reject Business">
                <Button
                  size="small"
                  icon={<FiXCircle />}
                  onClick={() => openDecision(record, 'reject')}
                  style={{
                    color: '#ffffff',
                    backgroundColor: '#F55D00',
                    borderColor: '#F55D00',
                    borderRadius: '8px',
                    fontWeight: '500',
                    height: '32px',
                    padding: '4px 12px'
                  }}
                  onMouseEnter={(e) => {
                    e.currentTarget.style.backgroundColor = '#2A2C41';
                    e.currentTarget.style.borderColor = '#2A2C41';
                  }}
                  onMouseLeave={(e) => {
                    e.currentTarget.style.backgroundColor = '#F55D00';
                    e.currentTarget.style.borderColor = '#F55D00';
                  }}
                >
                  Reject
                </Button>
              </Tooltip>
            )}
            {showPending && (
              <Tooltip title="Mark as Pending">
                <Button
                  size="small"
                  icon={<FiRefreshCw />}
                  onClick={() => openDecision(record, 'pending')}
                  style={{
                    color: '#2A2C41',
                    backgroundColor: '#f0f0f0',
                    borderColor: '#d9d9d9',
                    borderRadius: '8px',
                    fontWeight: '500',
                    height: '32px',
                    padding: '4px 12px'
                  }}
                >
                  Request Changes
                </Button>
              </Tooltip>
            )}
          </Space>
        );
      },
    },
  ];

  return (
    <div style={{
      backgroundColor: '#F4F4F8',
    }}>
      <style>
        {`
          .ant-modal .ant-modal-content {
            position: relative !important;
            background-color: white !important;
            background-clip: initial !important;
            border: none !important;
            border-radius: 16px !important;
            box-shadow: 0 4px 20px rgba(0,0,0,0.15) !important;
            pointer-events: auto !important;
            padding: 0px !important;
            max-height: 90vh !important;
            overflow-y: auto !important;
            margin-top: 50px;
            scrollbar-width: none !important; /* Firefox */
            -ms-overflow-style: none !important; /* IE and Edge */
          }
          .ant-modal .ant-modal-content::-webkit-scrollbar {
            display: none !important; /* Chrome, Safari and Opera */
          }
          .ant-modal .ant-modal-close {
            color: white !important;
            font-size: 10px !important;
            top: 2px !important;
            right: 16px !important;
          }
          .ant-modal .ant-modal-close:hover {
            color: #F55D00 !important;
          }
          .ant-modal .ant-modal-close .ant-modal-close-x {
            display: flex !important;
            align-items: center !important;
            justify-content: center !important;
            width: 32px !important;
            font-size: 18px !important;
          }
        `}
      </style>
      <div> {/* Reduced max width */}
        {/* Header */}
        <div style={{ marginBottom: '24px' }}>
          <div className="business-header-container" style={{
            display: 'flex',
            justifyContent: 'space-between',
            alignItems: 'center',
            gap: '20px',
            marginBottom: '8px',
            flexWrap: isMobile ? 'nowrap' : 'wrap'
          }}>
            <div style={{
              flex: isMobile ? '1 1 auto' : '1 1 auto',
              minWidth: isMobile ? '150px' : '200px'
            }}>
              <h1 style={{
                fontSize: isMobile ? '20px' : '30px',
                fontWeight: '700',
                color: '#2A2C41',
                margin: '0',
                letterSpacing: '-0.025em',
                lineHeight: '1.2'
              }}>
                Business Merchant KYC Verification
              </h1>

            </div>
            {/* View Toggle Buttons - Desktop Only */}
            {!isMobile && (
              <div style={{
                display: 'flex',
                alignItems: 'center',
                gap: '8px',
                background: 'white',
                padding: '4px',
                borderRadius: '8px',
                boxShadow: '0 2px 8px rgba(0,0,0,0.1)',
                border: '1px solid #e0e0e0'
              }}>
                <button
                  onClick={() => setViewMode('list')}
                  style={{
                    display: 'flex',
                    alignItems: 'center',
                    gap: '6px',
                    background: viewMode === 'list' ? '#F55D00' : 'transparent',
                    color: viewMode === 'list' ? 'white' : '#2A2C41',
                    border: 'none',
                    padding: '8px 16px',
                    borderRadius: '6px',
                    cursor: 'pointer',
                    fontSize: '14px',
                    fontWeight: '500',
                    transition: 'all 0.2s ease'
                  }}
                  onMouseEnter={(e) => {
                    if (viewMode !== 'list') {
                      e.currentTarget.style.background = 'rgba(245, 93, 0, 0.1)';
                    }
                  }}
                  onMouseLeave={(e) => {
                    if (viewMode !== 'list') {
                      e.currentTarget.style.background = 'transparent';
                    }
                  }}
                >
                  <FiList style={{ fontSize: '16px' }} />
                  List View
                </button>
                <button
                  onClick={() => setViewMode('grid')}
                  style={{
                    display: 'flex',
                    alignItems: 'center',
                    gap: '6px',
                    background: viewMode === 'grid' ? '#F55D00' : 'transparent',
                    color: viewMode === 'grid' ? 'white' : '#2A2C41',
                    border: 'none',
                    padding: '8px 16px',
                    borderRadius: '6px',
                    cursor: 'pointer',
                    fontSize: '14px',
                    fontWeight: '500',
                    transition: 'all 0.2s ease'
                  }}
                  onMouseEnter={(e) => {
                    if (viewMode !== 'grid') {
                      e.currentTarget.style.background = 'rgba(245, 93, 0, 0.1)';
                    }
                  }}
                  onMouseLeave={(e) => {
                    if (viewMode !== 'grid') {
                      e.currentTarget.style.background = 'transparent';
                    }
                  }}
                >
                  <FiGrid style={{ fontSize: '16px' }} />
                  Grid View
                </button>
              </div>
            )}
            {/* Export Button - Always visible */}
            <button
              onClick={exportToCSV}
              disabled={filteredBusinesses.length === 0}
              className="export-button"
              style={{
                alignItems: 'center',
                gap: isMobile ? '0' : '6px',
                background: filteredBusinesses.length === 0 ? '#dee2e6' : '#2A2C41',
                color: filteredBusinesses.length === 0 ? '#6c757d' : 'white',
                border: 'none',
                padding: isMobile ? '8px 12px' : '8px 12px',
                borderRadius: '6px',
                cursor: filteredBusinesses.length === 0 ? 'not-allowed' : 'pointer',
                fontSize: isMobile ? '11px' : '12px',
                fontWeight: '500',
                transition: 'all 0.2s ease',
                boxShadow: '0 2px 8px rgba(0,0,0,0.1)',
                minHeight: '36px',
                whiteSpace: 'nowrap'
              }}
              onMouseEnter={(e) => {
                if (filteredBusinesses.length > 0) {
                  e.target.style.background = '#1a1c2e';
                  e.target.style.transform = 'translateY(-1px)';
                }
              }}
              onMouseLeave={(e) => {
                if (filteredBusinesses.length > 0) {
                  e.target.style.background = '#2A2C41';
                  e.target.style.transform = 'translateY(0)';
                }
              }}
            >
              <FiDownload style={{ fontSize: '12px' }} />
              {!isMobile && ' Export'}
            </button>
          </div>
        </div>

        {/* Dynamic Statistics Cards */}
        <div className="static-cards" style={{
          display: 'grid',
          gridTemplateColumns: 'repeat(auto-fit, minmax(140px, 1fr))',
          gap: '12px',
          marginBottom: '20px'
        }}>
          {getStatisticsCards().map((stat, index) => (
            <div key={index} className="stats-card" style={{
              borderRadius: '12px',
              border: `1px solid ${stat.borderColor}`,
              boxShadow: `0 4px 12px ${stat.shadowColor}`,
              padding: '12px 8px',
              backgroundColor: 'white',
              display: 'flex',
              flexDirection: 'column',
              justifyContent: 'center',
              alignItems: 'center',
              textAlign: 'center',
              minHeight: '80px'
            }}>
              <div style={{ color: '#2A2C41', fontSize: '13px', marginBottom: '4px' }}>
                {stat.title}
              </div>
              <div style={{
                color: stat.color,
                fontSize: '20px',
                fontWeight: '700',
                lineHeight: '1'
              }}>
                {stat.value}
              </div>
            </div>
          ))}
        </div>

        {/* Filters */}
        <div style={{
          background: 'white',
          borderRadius: '12px',
          padding: '16px 20px',
          marginBottom: '20px',
          boxShadow: '0 2px 8px rgba(42, 44, 65, 0.04)',
          border: '1px solid rgba(42, 44, 65, 0.08)'
        }}>
          <div style={{
            display: 'flex',
            flexWrap: 'wrap',
            alignItems: 'flex-end',
            gap: '12px 16px'
          }}>
            {/* Search */}
            <div style={{ display: 'flex', flexDirection: 'column', flex: '1 1 180px', minWidth: '160px', maxWidth: '240px' }}>
              <label style={{ fontSize: '12px', fontWeight: '500', color: '#4b5563', marginBottom: '6px' }}>Search</label>
              <Input
                placeholder="Search by name, location..."
                prefix={<FiSearch style={{ color: '#9ca3af' }} />}
                value={filters.searchText}
                onChange={(e) => handleFilterChange('searchText', e.target.value)}
                style={{
                  height: '36px',
                  borderRadius: '6px',
                  borderColor: '#d1d5db',
                  fontSize: '13px'
                }}
              />
            </div>

            {/* Category */}
            <div style={{ display: 'flex', flexDirection: 'column', flex: '1 1 160px', minWidth: '140px', maxWidth: '200px' }}>
              <label style={{ fontSize: '12px', fontWeight: '500', color: '#4b5563', marginBottom: '6px' }}>Category</label>
              <select
                value={filters.category || 'all'}
                onChange={(e) => handleFilterChange('category', e.target.value)}
                style={{
                  width: '100%',
                  height: '36px',
                  borderRadius: '6px',
                  border: '1px solid #d1d5db',
                  padding: '0 10px',
                  fontSize: '13px',
                  backgroundColor: 'white',
                  color: '#1f2937',
                  cursor: 'pointer',
                  outline: 'none'
                }}
              >
                <option value="all">All Categories</option>
                {categories.length > 0 ? (
                  categories.map((cat, index) => {
                    const label = cat.type || cat.title || cat.business_type_name || cat.name || cat.type_name || cat.code;
                    const val = cat.type || cat.code || cat.business_type_id || cat.business_type || cat.id || label;
                    return (
                      <option key={index} value={val}>
                        {label}
                      </option>
                    );
                  })
                ) : (
                  <>
                    <option value="Retail_and_Wholesale">Retail & Wholesale</option>
                    <option value="Food_and_Beverage">Food & Beverage</option>
                    <option value="Fashion_Retail">Clothing & Fashions</option>
                    <option value="Pharmacy_and_Healthcare">Pharmacy & Healthcare</option>
                  </>
                )}
              </select>
            </div>

            {/* Status */}
            <div style={{ display: 'flex', flexDirection: 'column', flex: '1 1 140px', minWidth: '130px', maxWidth: '180px' }}>
              <label style={{ fontSize: '12px', fontWeight: '500', color: '#4b5563', marginBottom: '6px' }}>Status</label>
              <select
                value={filters.status || 'all'}
                onChange={(e) => handleFilterChange('status', e.target.value)}
                style={{
                  width: '100%',
                  height: '36px',
                  borderRadius: '6px',
                  border: '1px solid #d1d5db',
                  padding: '0 10px',
                  fontSize: '13px',
                  backgroundColor: 'white',
                  color: '#1f2937',
                  cursor: 'pointer',
                  outline: 'none'
                }}
              >
                <option value="all">All Status</option>
                <option value="pending">Pending</option>
                <option value="approved">Approved</option>
                <option value="rejected">Rejected</option>
                <option value="in_progress">In Progress</option>
              </select>
            </div>

            {/* Start Date */}
            <div style={{ display: 'flex', flexDirection: 'column', flex: '1 1 140px', minWidth: '130px', maxWidth: '170px' }}>
              <label style={{ fontSize: '12px', fontWeight: '500', color: '#4b5563', marginBottom: '6px' }}>Start Date</label>
              <DatePicker
                value={filters.dateRange?.[0]}
                onChange={(date) => handleFilterChange('dateRange', [date, filters.dateRange?.[1]])}
                style={{
                  width: '100%',
                  height: '36px',
                  borderRadius: '6px',
                  borderColor: '#d1d5db',
                  fontSize: '13px'
                }}
                placeholder="Start date"
              />
            </div>

            {/* End Date */}
            <div style={{ display: 'flex', flexDirection: 'column', flex: '1 1 140px', minWidth: '130px', maxWidth: '170px' }}>
              <label style={{ fontSize: '12px', fontWeight: '500', color: '#4b5563', marginBottom: '6px' }}>End Date</label>
              <DatePicker
                value={filters.dateRange?.[1]}
                onChange={(date) => handleFilterChange('dateRange', [filters.dateRange?.[0], date])}
                style={{
                  width: '100%',
                  height: '36px',
                  borderRadius: '6px',
                  borderColor: '#d1d5db',
                  fontSize: '13px'
                }}
                placeholder="End date"
              />
            </div>

            {/* Action Buttons */}
            <div style={{ display: 'flex', alignItems: 'center', gap: '8px', marginLeft: 'auto' }}>
              <button
                onClick={clearFilters}
                style={{
                  height: '36px',
                  padding: '0 16px',
                  borderRadius: '6px',
                  backgroundColor: '#64748b',
                  color: 'white',
                  border: 'none',
                  fontSize: '13px',
                  fontWeight: '500',
                  cursor: 'pointer',
                  display: 'flex',
                  alignItems: 'center',
                  gap: '6px',
                  whiteSpace: 'nowrap',
                  transition: 'background 0.2s'
                }}
              >
                Clear Filters
              </button>

              <button
                onClick={loadBusinesses}
                disabled={loading}
                style={{
                  height: '36px',
                  padding: '0 16px',
                  borderRadius: '6px',
                  backgroundColor: '#F55D00',
                  color: 'white',
                  border: 'none',
                  fontSize: '13px',
                  fontWeight: '500',
                  cursor: loading ? 'not-allowed' : 'pointer',
                  display: 'flex',
                  alignItems: 'center',
                  gap: '6px',
                  whiteSpace: 'nowrap',
                  opacity: loading ? 0.7 : 1,
                  transition: 'background 0.2s'
                }}
              >
                <FiRefreshCw className={loading ? 'spin' : ''} size={14} /> Refresh
              </button>
            </div>
          </div>
        </div>

        {/* Business Table/Grid View */}
        {(!isMobile && viewMode === 'list') ? (
          <Card style={{
            borderRadius: '12px',
            border: '1px solid rgba(42, 44, 65, 0.08)',
            boxShadow: '0 2px 8px rgba(42, 44, 65, 0.04)'
          }}>
            {/* Bulk Actions */}
            {selectedRowKeys.length > 0 && (
              <div style={{
                padding: '16px',
                backgroundColor: 'rgba(42, 44, 65, 0.05)',
                borderBottom: '1px solid rgba(42, 44, 65, 0.08)',
                display: 'flex',
                justifyContent: 'space-between',
                alignItems: 'center'
              }}>
                <span style={{
                  fontSize: '14px',
                  fontWeight: '600',
                  color: '#2A2C41'
                }}>
                  {selectedRowKeys.length} business{selectedRowKeys.length > 1 ? 'es' : ''} selected
                </span>
                <Space>
                  <Button
                    icon={<FiCheckCircle />}
                    onClick={() => handleBulkAction('approve')}
                    loading={bulkActionLoading}
                    style={{
                      backgroundColor: '#FDBF50',
                      borderColor: '#FDBF50',
                      color: '#ffffff',
                      borderRadius: '8px',
                      fontWeight: '500'
                    }}
                  >
                    Bulk Approve
                  </Button>
                  <Button
                    icon={<FiXCircle />}
                    onClick={() => handleBulkAction('reject')}
                    loading={bulkActionLoading}
                    style={{
                      backgroundColor: '#F55D00',
                      borderColor: '#F55D00',
                      color: '#ffffff',
                      borderRadius: '8px',
                      fontWeight: '500'
                    }}
                  >
                    Bulk Reject
                  </Button>
                  <Button
                    onClick={() => setSelectedRowKeys([])}
                    style={{
                      borderRadius: '8px',
                      borderColor: '#2A2C41',
                      color: '#2A2C41'
                    }}
                  >
                    Clear Selection
                  </Button>
                </Space>
              </div>
            )}

            <Table
              columns={columns}
              dataSource={paginatedBusinesses}
              rowKey={(row) => row?.application_id || row?.id}
              loading={loading}
              rowSelection={rowSelection}
              pagination={false}
              style={{
                borderRadius: '12px'
              }}
              scroll={{ x: 1000 }}
            />
          </Card>
        ) : (
          /* Grid View */
          <div>
            {loading ? (
              <div style={{ textAlign: 'center', padding: '40px' }}>
                <span>Loading...</span>
              </div>
            ) : (
              <Row gutter={[16, 16]}>
                {paginatedBusinesses.map((business) => {
                  const raw = String(business.rawStatus || '').toLowerCase();
                  const statusGroup = (
                    raw === 'approved' ? 'approved' :
                      raw === 'rejected' ? 'rejected' :
                        (raw === 'pending' || raw === 'submitted' || raw === 'under_review' || raw === 'under review') ? 'pending' :
                          (raw === 'in_progress' || raw === 'requires_changes' || raw === 'request_changes') ? 'in-progress' :
                            'pending'
                  );
                  const isRequiresChanges = raw === 'requires_changes' || raw === 'request_changes' || raw === 'changes_requested';
                  const showApprove = statusGroup === 'pending' || statusGroup === 'rejected' || isRequiresChanges;
                  const showReject = statusGroup === 'pending' || statusGroup === 'in-progress' || isRequiresChanges || statusGroup === 'approved';
                  const showPending = statusGroup === 'in-progress' || statusGroup === 'rejected' || isRequiresChanges;

                  const isRejected = business.status === 'Rejected';
                  const isReqChanges = business.status === 'Requires Changes';
                  const items = isRejected ? (business.rejection_reasons || []) : (isReqChanges ? (business.required_changes || []) : []);

                  let badgeStatus = 'processing';
                  let textColor = '#1677FF';

                  if (business.status === 'Approved') {
                    badgeStatus = 'success';
                    textColor = '#52C41A';
                  } else if (business.status === 'Rejected') {
                    badgeStatus = 'error';
                    textColor = '#f50000ff';
                  }

                  return (
                    <Col xs={12} sm={12} md={12} lg={6} xl={6} key={business.id}>
                      <Card
                        style={{
                          borderRadius: '12px',
                          border: '1px solid rgba(42, 44, 65, 0.08)',
                          boxShadow: '0 2px 8px rgba(42, 44, 65, 0.04)',
                          height: '100%',
                          display: 'flex',
                          flexDirection: 'column',
                          transition: 'all 0.3s ease'
                        }}
                        bodyStyle={{ padding: '16px', flex: 1, display: 'flex', flexDirection: 'column' }}
                        hoverable
                      >
                        {/* Header: Business Name & Status */}
                        <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'flex-start', marginBottom: '12px' }}>
                          <div style={{ flex: 1, minWidth: 0 }}>
                            <div style={{ fontWeight: '700', color: '#2A2C41', fontSize: '16px', marginBottom: '4px', lineHeight: '1.3' }}>
                              {business.businessName}
                            </div>
                            <div style={{ fontSize: '12px', color: '#6c757d', display: 'flex', alignItems: 'center', gap: '4px' }}>
                              <FiUser style={{ fontSize: '11px' }} />
                              {business.ownerName}
                            </div>
                          </div>
                          {/* Status Badge & Category - Top Right */}
                          <div style={{ display: 'flex', flexDirection: 'column', gap: '6px', alignItems: 'flex-end', marginLeft: '8px', flexShrink: 0 }}>
                            {/* Status Badge */}
                            <div style={{
                              padding: '4px 8px',
                              borderRadius: '6px',
                              display: 'flex',
                              alignItems: 'center',
                              gap: '4px'
                            }}>
                              <Badge status={badgeStatus} />
                              <span style={{
                                color: textColor,
                                fontWeight: '600',
                                fontSize: '11px',
                                whiteSpace: 'nowrap'
                              }}>
                                {business.status}
                              </span>
                            </div>
                            {/* Business Type Tag */}
                            <Tag
                              color={getCategoryColor(business.category)}
                              style={{
                                borderRadius: '6px',
                                fontWeight: '500',
                                fontSize: '11px',
                                padding: '2px 10px',
                                margin: 0
                              }}
                            >
                              {business.businessType || getCategoryLabel(business.category)}
                            </Tag>
                          </div>
                        </div>

                        {/* Info Section */}
                        <div style={{ flex: 1, marginBottom: '12px' }}>
                          {/* Location */}
                          <div style={{ display: 'flex', alignItems: 'flex-start', gap: '6px', marginBottom: '6px' }}>
                            <FiMapPin style={{ color: '#6c757d', fontSize: '12px', marginTop: '2px', flex: '0 0 auto' }} />
                            <span style={{
                              fontSize: '12px',
                              color: '#2A2C41',
                              lineHeight: '1.4',
                              overflow: 'hidden',
                              textOverflow: 'ellipsis',
                              display: '-webkit-box',
                              WebkitLineClamp: 2,
                              WebkitBoxOrient: 'vertical'
                            }}>
                              {business.location}
                            </span>
                          </div>

                          {/* Submission Date */}
                          <div style={{ display: 'flex', alignItems: 'center', gap: '6px', marginBottom: '8px' }}>
                            <FiCalendar style={{ color: '#6c757d', fontSize: '12px' }} />
                            <span style={{ fontSize: '12px', color: '#2A2C41' }}>
                              {business.submissionDate ? dayjs(business.submissionDate).format('MMM DD, YYYY') : '—'}
                            </span>
                          </div>

                          {/* Notes */}
                          {items && items.length > 0 && (
                            <div style={{ marginTop: '8px' }}>
                              <div style={{ fontSize: '10px', color: '#6c757d', marginBottom: '4px', fontWeight: '500' }}>
                                Notes:
                              </div>
                              <div style={{ display: 'flex', gap: '4px', flexWrap: 'wrap' }}>
                                {items.slice(0, 2).map((t, idx) => (
                                  <Tag key={idx} color={isRejected ? '#F55D00' : '#FDBF50'} style={{ borderRadius: '4px', fontSize: '9px', margin: 0, padding: '2px 6px' }}>
                                    {String(t)}
                                  </Tag>
                                ))}
                                {items.length > 2 && (
                                  <Tag style={{ borderRadius: '4px', fontSize: '9px', margin: 0, padding: '2px 6px' }}>+{items.length - 2}</Tag>
                                )}
                              </div>
                            </div>
                          )}
                        </div>

                        {/* Actions */}
                        <div style={{ display: 'flex', gap: '8px', flexWrap: 'wrap', marginTop: 'auto' }}>
                          <Button
                            icon={<FiEye style={{ marginRight: '6px' }} />}
                            onClick={() => viewBusinessDetails(business)}
                            style={{
                              color: '#2A2C41',
                              backgroundColor: '#ffffff',
                              borderColor: '#2A2C41',
                              border: '1.5px solid #2A2C41',
                              borderRadius: '6px',
                              fontWeight: '500',
                              padding: '4px 8px',
                              height: '28px',
                              fontSize: '11px',
                              flex: 1,
                              display: 'flex',
                              alignItems: 'center',
                              justifyContent: 'center',
                              gap: '4px'
                            }}
                            onMouseEnter={(e) => {
                              e.currentTarget.style.backgroundColor = '#2A2C41';
                              e.currentTarget.style.color = '#ffffff';
                            }}
                            onMouseLeave={(e) => {
                              e.currentTarget.style.backgroundColor = '#ffffff';
                              e.currentTarget.style.color = '#2A2C41';
                            }}
                          >
                            View
                          </Button>
                          {showApprove && (
                            <Button
                              size="small"
                              icon={<FiCheckCircle style={{ marginRight: '6px' }} />}
                              onClick={() => openDecision(business, 'approve')}
                              style={{
                                color: '#ffffff',
                                backgroundColor: '#FDBF50',
                                borderColor: '#FDBF50',
                                borderRadius: '6px',
                                fontWeight: '500',
                                height: '28px',
                                padding: '4px 8px',
                                fontSize: '11px',
                                flex: 1,
                                display: 'flex',
                                alignItems: 'center',
                                justifyContent: 'center',
                                gap: '4px'
                              }}
                            >
                              Approve
                            </Button>
                          )}
                          {showReject && (
                            <Button
                              size="small"
                              icon={<FiXCircle style={{ marginRight: '6px' }} />}
                              onClick={() => openDecision(business, 'reject')}
                              style={{
                                color: '#ffffff',
                                backgroundColor: '#F55D00',
                                borderColor: '#F55D00',
                                borderRadius: '6px',
                                fontWeight: '500',
                                height: '28px',
                                padding: '4px 8px',
                                fontSize: '11px',
                                flex: 1,
                                display: 'flex',
                                alignItems: 'center',
                                justifyContent: 'center',
                                gap: '4px'
                              }}
                            >
                              Reject
                            </Button>
                          )}
                          {showPending && (
                            <Button
                              size="small"
                              icon={<FiRefreshCw style={{ marginRight: '6px' }} />}
                              onClick={() => openDecision(business, 'pending')}
                              style={{
                                color: '#2A2C41',
                                backgroundColor: '#f0f0f0',
                                borderColor: '#d9d9d9',
                                borderRadius: '6px',
                                fontWeight: '500',
                                height: '28px',
                                padding: '4px 8px',
                                fontSize: '11px',
                                flex: 1,
                                display: 'flex',
                                alignItems: 'center',
                                justifyContent: 'center',
                                gap: '4px'
                              }}
                            >
                              Pending
                            </Button>
                          )}
                        </div>
                      </Card>
                    </Col>
                  );
                })}
              </Row>
            )}
          </div>
        )}

        {/* Pagination at End of Page */}
        {totalPages > 1 && (() => {
          const current = currentPage || 1;
          const total = totalPages || 1;

          // Build page list with ellipsis
          const pages = Array.from({ length: total }, (_, i) => i + 1)
            .filter(p => p === 1 || p === total || Math.abs(p - current) <= 1)
            .reduce((acc, p, idx, arr) => {
              if (idx > 0 && p - arr[idx - 1] > 1) acc.push('...');
              acc.push(p);
              return acc;
            }, []);

          const btnBase = {
            width: '44px',
            height: '44px',
            borderRadius: '10px',
            border: '1px solid #e5e7eb',
            background: 'white',
            cursor: 'pointer',
            fontSize: '15px',
            fontWeight: '600',
            color: '#374151',
            display: 'flex',
            alignItems: 'center',
            justifyContent: 'center',
            transition: 'all 0.15s ease',
            boxShadow: '0 1px 3px rgba(0,0,0,0.06)'
          };

          return (
            <div style={{
              display: 'flex',
              justifyContent: 'center',
              alignItems: 'center',
              gap: '6px',
              padding: '24px 0 16px'
            }} className="pagination-container">
              {/* Prev */}
              <button
                onClick={() => handlePageChange(current - 1)}
                disabled={current <= 1}
                style={{
                  ...btnBase,
                  cursor: current <= 1 ? 'not-allowed' : 'pointer',
                  color: current <= 1 ? '#d1d5db' : '#374151'
                }}
              >
                <FaChevronLeft size={13} />
              </button>

              {/* Page numbers */}
              {pages.map((item, idx) =>
                item === '...' ? (
                  <span key={`ellipsis-${idx}`} style={{
                    width: '44px',
                    height: '44px',
                    display: 'flex',
                    alignItems: 'center',
                    justifyContent: 'center',
                    fontSize: '15px',
                    color: '#9ca3af',
                    letterSpacing: '2px'
                  }}>
                    ...
                  </span>
                ) : (
                  <button
                    key={item}
                    onClick={() => handlePageChange(item)}
                    style={{
                      ...btnBase,
                      background: current === item ? '#F55D00' : 'white',
                      color: current === item ? 'white' : '#374151',
                      border: current === item ? '1px solid #F55D00' : '1px solid #e5e7eb',
                      boxShadow: current === item
                        ? '0 4px 12px rgba(245, 93, 0, 0.3)'
                        : '0 1px 3px rgba(0,0,0,0.06)'
                    }}
                  >
                    {item}
                  </button>
                )
              )}

              {/* Next */}
              <button
                onClick={() => handlePageChange(current + 1)}
                disabled={current >= total}
                style={{
                  ...btnBase,
                  cursor: current >= total ? 'not-allowed' : 'pointer',
                  color: current >= total ? '#d1d5db' : '#374151'
                }}
              >
                <FaChevronRight size={13} />
              </button>
            </div>
          );
        })()}

        {/* CSS for hiding scrollbar */}
        <style>
          {`
            .modal-content-scrollable::-webkit-scrollbar {
              display: none;
            }
          `}
        </style>

        {/* Business Detail Modal */}
        <Modal
          title={null}
          open={detailModalVisible}
          onCancel={() => setDetailModalVisible(false)}
          footer={null}
          width={900}
          centered
          styles={{
            body: { padding: 0 },
            content: { borderRadius: '20px', overflow: 'hidden', boxShadow: '0 20px 60px rgba(0, 0, 0, 0.15)' }
          }}
        >
          {selectedBusiness && (
            <div style={{ backgroundColor: '#ffffff', padding: '0 !important', height: '90vh', display: 'flex', flexDirection: 'column' }}>
              {/* Debug log */}

              {/* Modal Header */}
              <div style={{
                padding: '20px 28px',
                backgroundColor: '#DE7E07',
                position: 'sticky',
                top: 0,
                zIndex: 10,
                flexShrink: 0
              }}>
                <div style={{
                  display: 'flex',
                  justifyContent: 'space-between',
                  alignItems: 'center',
                  gap: '20px',
                  flexWrap: 'wrap'
                }}>
                  {/* Left Side - Store Name & Type */}
                  <div>
                    <div style={{ display: 'flex', alignItems: 'center', gap: '10px', flexWrap: 'wrap' }}>
                      <h2 style={{
                        margin: '0',
                        fontSize: '22px',
                        fontWeight: '700',
                        color: '#ffffff',
                        letterSpacing: '-0.025em'
                      }}>
                        {selectedBusiness.businessName}
                      </h2>
                      <Tag
                        color={getCategoryColor(selectedBusiness.category)}
                        style={{
                          borderRadius: '6px',
                          fontWeight: '600',
                          fontSize: '11px',
                          textTransform: 'uppercase',
                          letterSpacing: '0.5px'
                        }}
                      >
                        {selectedBusiness.businessType || getCategoryLabel(selectedBusiness.category)}
                      </Tag>
                    </div>
                    <div style={{ color: 'rgba(255, 255, 255, 0.7)', fontSize: '12px', marginTop: '4px' }}>
                      App ID: <span style={{ color: '#ffffff', fontFamily: 'monospace' }}>{selectedBusiness.applicationId}</span>
                    </div>
                  </div>

                  {/* Right Side - Owner & Status */}
                  <div style={{ textAlign: 'right', display: 'flex', flexDirection: 'column', alignItems: 'flex-end', gap: '4px' }}>
                    <Tag
                      color={
                        selectedBusiness.status === 'Approved' ? '#52c41a' :
                          selectedBusiness.status === 'Rejected' ? '#ff4d4f' :
                            selectedBusiness.status === 'Requires Changes' ? '#faad14' : '#1890ff'
                      }
                      style={{
                        margin: 0,
                        fontWeight: '600',
                        borderRadius: '6px',
                        padding: '2px 10px',
                        fontSize: '12px'
                      }}
                    >
                      {selectedBusiness.status}
                    </Tag>
                    <div style={{
                      fontSize: '13px',
                      color: 'rgba(255, 255, 255, 0.9)',
                      fontWeight: '500'
                    }}>
                      Owner: {selectedBusiness.ownerName}
                    </div>
                  </div>
                </div>
              </div>

              {/* Modal Content */}
              <div
                className="modal-content-scrollable"
                style={{
                  padding: '20px',
                  flex: 1,
                  overflowY: 'auto',
                  scrollbarWidth: 'none', /* Firefox */
                  msOverflowStyle: 'none', /* Internet Explorer 10+ */
                }}
              >
                {/* Reasons / Changes Banner */}
                {selectedBusiness && (
                  (() => {
                    const isRejected = selectedBusiness.status === 'Rejected';
                    const isReqChanges = selectedBusiness.status === 'Requires Changes';
                    const hasReasons = Array.isArray(selectedBusiness.rejection_reasons) && selectedBusiness.rejection_reasons.length > 0;
                    const hasChanges = Array.isArray(selectedBusiness.required_changes) && selectedBusiness.required_changes.length > 0;
                    const hasComments = typeof selectedBusiness.comments === 'string' && selectedBusiness.comments.trim();
                    if (!isRejected && !isReqChanges && !hasComments) return null;
                    return (
                      <div style={{
                        backgroundColor: isRejected ? 'rgba(245, 93, 0, 0.08)' : 'rgba(253, 191, 80, 0.12)',
                        border: `1px solid ${isRejected ? '#F55D00' : '#FDBF50'}`,
                        borderRadius: '12px',
                        padding: '16px',
                        marginBottom: '20px'
                      }}>
                        {hasComments && (
                          <div style={{ marginBottom: (hasReasons || hasChanges) ? '8px' : 0 }}>
                            <div style={{ fontWeight: 600, color: '#2A2C41', marginBottom: '6px' }}>Admin Comments</div>
                            <div style={{ color: '#2A2C41' }}>{selectedBusiness.comments}</div>
                          </div>
                        )}
                        {isRejected && hasReasons && (
                          <div>
                            <div style={{ fontWeight: 600, color: '#2A2C41', marginBottom: '6px' }}>Rejection Reasons</div>
                            <div style={{ display: 'flex', flexWrap: 'wrap', gap: '8px' }}>
                              {selectedBusiness.rejection_reasons.map((r, idx) => (
                                <Tag key={idx} color="#F55D00" style={{ borderRadius: '8px' }}>
                                  {String(r)}
                                </Tag>
                              ))}
                            </div>
                          </div>
                        )}
                        {isReqChanges && hasChanges && (
                          <div>
                            <div style={{ fontWeight: 600, color: '#2A2C41', marginBottom: '6px' }}>Required Changes</div>
                            <div style={{ display: 'flex', flexWrap: 'wrap', gap: '8px' }}>
                              {selectedBusiness.required_changes.map((c, idx) => (
                                <Tag key={idx} color="#FDBF50" style={{ borderRadius: '8px' }}>
                                  {String(c)}
                                </Tag>
                              ))}
                            </div>
                          </div>
                        )}
                      </div>
                    );
                  })()
                )}

                {/* First Row - Contact Information and Registration Details */}
                <Row gutter={[16, 16]} style={{ marginBottom: '16px' }}>
                  {/* Contact Information */}
                  <Col xs={24} md={12}>
                    <div style={{
                      backgroundColor: 'white',
                      borderRadius: '8px',
                      padding: '14px',
                      border: '1px solid rgba(0, 0, 0, 0.08)',
                      height: '100%',
                      boxShadow: '0 2px 4px rgba(0, 0, 0, 0.04)'
                    }}>
                      <h4 style={{
                        margin: '0 0 10px 0',
                        fontSize: '14px',
                        fontWeight: '600',
                        color: '#1f2937',
                        display: 'flex',
                        alignItems: 'center',
                        gap: '6px'
                      }}>
                        <FiPhone style={{ fontSize: '14px', color: '#F55D00' }} />
                        Contact Information
                      </h4>
                      <div>
                        <div style={{ display: 'flex', alignItems: 'center', gap: '6px', marginBottom: '6px', fontSize: '12px', color: '#374151' }}>
                          <span style={{ fontWeight: '500', color: '#6b7280', minWidth: '60px', fontSize: '11px' }}>Owner:</span>
                          <span style={{ fontWeight: '500' }}>{selectedBusiness.ownerName}</span>
                        </div>
                        <div style={{ display: 'flex', alignItems: 'center', gap: '6px', marginBottom: '6px', fontSize: '12px', color: '#374151' }}>
                          <span style={{ fontWeight: '500', color: '#6b7280', minWidth: '60px', fontSize: '11px' }}>Phone:</span>
                          <span>{selectedBusiness.phone}</span>
                        </div>
                        <div style={{ display: 'flex', alignItems: 'center', gap: '6px', marginBottom: selectedBusiness.businessWhatsapp ? '6px' : 0, fontSize: '12px', color: '#374151' }}>
                          <span style={{ fontWeight: '500', color: '#6b7280', minWidth: '60px', fontSize: '11px' }}>Email:</span>
                          <span>{selectedBusiness.email}</span>
                        </div>
                        {selectedBusiness.businessWhatsapp && (
                          <div style={{ display: 'flex', alignItems: 'center', gap: '6px', fontSize: '12px', color: '#374151' }}>
                            <span style={{ fontWeight: '500', color: '#6b7280', minWidth: '60px', fontSize: '11px' }}>WhatsApp:</span>
                            <span>{selectedBusiness.businessWhatsapp}</span>
                          </div>
                        )}
                      </div>
                    </div>
                  </Col>

                  {/* Registration Details */}
                  <Col xs={24} md={12}>
                    <div style={{
                      backgroundColor: 'white',
                      borderRadius: '8px',
                      padding: '14px',
                      border: '1px solid rgba(0, 0, 0, 0.08)',
                      height: '100%',
                      boxShadow: '0 2px 4px rgba(0, 0, 0, 0.04)'
                    }}>
                      <h4 style={{
                        margin: '0 0 10px 0',
                        fontSize: '14px',
                        fontWeight: '600',
                        color: '#1f2937',
                        display: 'flex',
                        alignItems: 'center',
                        gap: '6px'
                      }}>
                        <FiClipboard style={{ fontSize: '14px', color: '#F55D00' }} />
                        Registration Details
                      </h4>
                      <div>
                        <div style={{ display: 'flex', alignItems: 'center', gap: '6px', marginBottom: '6px', fontSize: '12px', color: '#374151' }}>
                          <span style={{ fontWeight: '500', color: '#6b7280', minWidth: '60px', fontSize: '11px' }}>App ID:</span>
                          <span style={{ fontFamily: 'monospace' }}>{selectedBusiness.applicationId}</span>
                        </div>
                        <div style={{ display: 'flex', alignItems: 'center', gap: '6px', marginBottom: '6px', fontSize: '12px', color: '#374151' }}>
                          <span style={{ fontWeight: '500', color: '#6b7280', minWidth: '60px', fontSize: '11px' }}>Reg. No:</span>
                          <span>{selectedBusiness.registrationNumber}</span>
                        </div>
                        <div style={{ display: 'flex', alignItems: 'center', gap: '6px', marginBottom: '6px', fontSize: '12px', color: '#374151' }}>
                          <span style={{ fontWeight: '500', color: '#6b7280', minWidth: '60px', fontSize: '11px' }}>GSTIN:</span>
                          <span>{selectedBusiness.gstin}</span>
                        </div>
                        {selectedBusiness.fssaiNumber && selectedBusiness.fssaiNumber !== '—' && (
                          <div style={{ display: 'flex', alignItems: 'center', gap: '6px', marginBottom: '6px', fontSize: '12px', color: '#374151' }}>
                            <span style={{ fontWeight: '500', color: '#6b7280', minWidth: '60px', fontSize: '11px' }}>FSSAI:</span>
                            <span>{selectedBusiness.fssaiNumber}</span>
                          </div>
                        )}
                        <div style={{ display: 'flex', alignItems: 'center', gap: '6px', fontSize: '12px', color: '#374151' }}>
                          <span style={{ fontWeight: '500', color: '#6b7280', minWidth: '60px', fontSize: '11px' }}>Submitted:</span>
                          <span>{selectedBusiness.submissionDate ? dayjs(selectedBusiness.submissionDate).format('MMM DD, YYYY') : '—'}</span>
                        </div>
                      </div>
                    </div>
                  </Col>
                </Row>

                {/* Second Row - Business Operations and Business Info */}
                <Row gutter={[16, 16]}>
                  {/* Business Operations */}
                  <Col xs={24} md={12}>
                    <div style={{
                      backgroundColor: 'white',
                      borderRadius: '8px',
                      padding: '14px',
                      border: '1px solid rgba(0, 0, 0, 0.08)',
                      height: '100%',
                      boxShadow: '0 2px 4px rgba(0, 0, 0, 0.04)'
                    }}>
                      <h4 style={{
                        margin: '0 0 10px 0',
                        fontSize: '14px',
                        fontWeight: '600',
                        color: '#1f2937',
                        display: 'flex',
                        alignItems: 'center',
                        gap: '6px'
                      }}>
                        <FiSettings style={{ fontSize: '14px', color: '#F55D00' }} />
                        Business Operations
                      </h4>
                      <div>
                        <div style={{ display: 'flex', alignItems: 'center', gap: '6px', marginBottom: '6px', fontSize: '12px', color: '#374151' }}>
                          <span style={{ fontWeight: '500', color: '#6b7280', minWidth: '85px', fontSize: '11px' }}>Working Hours:</span>
                          {typeof selectedBusiness.workingHours === 'object' && selectedBusiness.workingHours !== null ? (
                            <Tooltip
                              title={
                                <div style={{ fontSize: '11px', padding: '4px', minWidth: '150px' }}>
                                  <div style={{ fontWeight: 600, marginBottom: '4px', borderBottom: '1px solid rgba(255,255,255,0.2)', paddingBottom: '2px' }}>Weekly Schedule</div>
                                  {['mon', 'tue', 'wed', 'thu', 'fri', 'sat', 'sun'].map((day) => {
                                    const matchingKey = Object.keys(selectedBusiness.workingHours).find(k => k.toLowerCase() === day || k.toLowerCase().startsWith(day));
                                    const val = matchingKey ? selectedBusiness.workingHours[matchingKey] : null;
                                    const dayName = { mon: 'Mon', tue: 'Tue', wed: 'Wed', thu: 'Thu', fri: 'Fri', sat: 'Sat', sun: 'Sun' }[day];
                                    let timeStr = 'Closed';
                                    if (Array.isArray(val) && val.length > 0) {
                                      timeStr = val.map(s => `${s.open} - ${s.close}`).join(', ');
                                    } else if (val && val.open && val.close) {
                                      timeStr = `${val.open} - ${val.close}`;
                                    }
                                    return (
                                      <div key={day} style={{ display: 'flex', justifyContent: 'space-between', gap: '12px', padding: '1px 0' }}>
                                        <span style={{ fontWeight: 600 }}>{dayName}:</span>
                                        <span>{timeStr}</span>
                                      </div>
                                    );
                                  })}
                                </div>
                              }
                            >
                              <span style={{ cursor: 'pointer', borderBottom: '1px dotted #9ca3af' }}>
                                {formatWorkingHours(selectedBusiness.workingHours)}
                              </span>
                            </Tooltip>
                          ) : (
                            <span>{formatWorkingHours(selectedBusiness.workingHours)}</span>
                          )}
                        </div>
                        <div style={{ display: 'flex', alignItems: 'center', gap: '6px', marginBottom: '6px', fontSize: '12px', color: '#374151' }}>
                          <span style={{ fontWeight: '500', color: '#6b7280', minWidth: '85px', fontSize: '11px' }}>Order Types:</span>
                          <span>{formatDeliveryPreferences(selectedBusiness.deliveryPreferences, selectedBusiness.orderTypes)}</span>
                        </div>
                        <div style={{ display: 'flex', alignItems: 'center', gap: '6px', marginBottom: '6px', fontSize: '12px', color: '#374151' }}>
                          <span style={{ fontWeight: '500', color: '#6b7280', minWidth: '85px', fontSize: '11px' }}>Payment Modes:</span>
                          <span>{formatPaymentModes(selectedBusiness.paymentModes, selectedBusiness.upiVpa, selectedBusiness.isCodAvailable)}</span>
                        </div>
                        {selectedBusiness.upiVpa && (
                          <div style={{ display: 'flex', alignItems: 'center', gap: '6px', marginBottom: '6px', fontSize: '12px', color: '#374151' }}>
                            <span style={{ fontWeight: '500', color: '#6b7280', minWidth: '85px', fontSize: '11px' }}>UPI VPA:</span>
                            <span style={{ fontFamily: 'monospace' }}>{selectedBusiness.upiVpa}</span>
                          </div>
                        )}
                        {selectedBusiness.isCodAvailable !== null && selectedBusiness.isCodAvailable !== undefined && (
                          <div style={{ display: 'flex', alignItems: 'center', gap: '6px', fontSize: '12px', color: '#374151' }}>
                            <span style={{ fontWeight: '500', color: '#6b7280', minWidth: '85px', fontSize: '11px' }}>COD Available:</span>
                            <Tag color={selectedBusiness.isCodAvailable ? 'green' : 'default'} style={{ margin: 0, fontSize: '11px', lineHeight: '18px', padding: '0 6px' }}>
                              {selectedBusiness.isCodAvailable ? 'Yes' : 'No'}
                            </Tag>
                          </div>
                        )}
                      </div>
                    </div>
                  </Col>

                  {/* Business & Banking Info */}
                  <Col xs={24} md={12}>
                    <div style={{
                      backgroundColor: 'white',
                      borderRadius: '8px',
                      padding: '14px',
                      border: '1px solid rgba(0, 0, 0, 0.08)',
                      height: '100%',
                      boxShadow: '0 2px 4px rgba(0, 0, 0, 0.04)'
                    }}>
                      <h4 style={{
                        margin: '0 0 10px 0',
                        fontSize: '14px',
                        fontWeight: '600',
                        color: '#1f2937',
                        display: 'flex',
                        alignItems: 'center',
                        gap: '6px'
                      }}>
                        <FiMapPin style={{ fontSize: '14px', color: '#F55D00' }} />
                        Business & Financial Details
                      </h4>
                      <div>
                        <div style={{ display: 'flex', alignItems: 'center', gap: '6px', marginBottom: '6px', fontSize: '12px', color: '#374151' }}>
                          <span style={{ fontWeight: '500', color: '#6b7280', minWidth: '65px', fontSize: '11px' }}>Type:</span>
                          <span>{selectedBusiness.businessType || getCategoryLabel(selectedBusiness.category)}</span>
                        </div>
                        <div style={{ display: 'flex', alignItems: 'flex-start', gap: '6px', marginBottom: '6px', fontSize: '12px', color: '#374151' }}>
                          <span style={{ fontWeight: '500', color: '#6b7280', minWidth: '65px', fontSize: '11px' }}>Address:</span>
                          <span style={{ wordBreak: 'break-word' }} title={selectedBusiness.businessAddress}>{selectedBusiness.businessAddress}</span>
                        </div>
                        {selectedBusiness.accountHolderName && selectedBusiness.accountHolderName !== '—' && (
                          <div style={{ display: 'flex', alignItems: 'center', gap: '6px', marginBottom: '6px', fontSize: '12px', color: '#374151' }}>
                            <span style={{ fontWeight: '500', color: '#6b7280', minWidth: '65px', fontSize: '11px' }}>A/C Holder:</span>
                            <span>{selectedBusiness.accountHolderName}</span>
                          </div>
                        )}
                        <div style={{ display: 'flex', alignItems: 'center', gap: '6px', marginBottom: '6px', fontSize: '12px', color: '#374151' }}>
                          <span style={{ fontWeight: '500', color: '#6b7280', minWidth: '65px', fontSize: '11px' }}>Bank A/C:</span>
                          <span style={{ fontFamily: 'monospace' }}>{selectedBusiness.bankAccountNumber}</span>
                        </div>
                        <div style={{ display: 'flex', alignItems: 'center', gap: '6px', marginBottom: '6px', fontSize: '12px', color: '#374151' }}>
                          <span style={{ fontWeight: '500', color: '#6b7280', minWidth: '65px', fontSize: '11px' }}>IFSC:</span>
                          <span style={{ fontFamily: 'monospace' }}>{selectedBusiness.ifscCode}</span>
                        </div>
                        <div style={{ display: 'flex', alignItems: 'center', gap: '6px', fontSize: '12px', color: '#374151' }}>
                          <span style={{ fontWeight: '500', color: '#6b7280', minWidth: '65px', fontSize: '11px' }}>PAN:</span>
                          <span style={{ fontFamily: 'monospace' }}>{selectedBusiness.ownerPan}</span>
                        </div>
                      </div>
                    </div>
                  </Col>

                  {/* Submission Timeline */}
                  {selectedBusiness.submissionTimeline && selectedBusiness.submissionTimeline.length > 0 && (
                    <Col span={24}>
                      <div style={{
                        backgroundColor: '#F4F4F8',
                        borderRadius: '16px',
                        padding: '20px',
                        border: '1px solid rgba(42, 44, 65, 0.1)',
                        marginBottom: '16px'
                      }}>
                        <h4 style={{
                          margin: '0 0 16px 0',
                          fontSize: '16px',
                          fontWeight: '600',
                          color: '#2A2C41',
                          display: 'flex',
                          alignItems: 'center',
                          gap: '8px'
                        }}>
                          <FiClock style={{ fontSize: '18px', color: '#2A2C41' }} />
                          Submission Timeline
                        </h4>
                        <div>
                          {selectedBusiness.submissionTimeline.map((item, idx) => (
                            <div key={idx} style={{
                              display: 'flex',
                              alignItems: 'flex-start',
                              gap: '12px',
                              marginBottom: idx < selectedBusiness.submissionTimeline.length - 1 ? '12px' : 0
                            }}>
                              <div style={{
                                width: '8px',
                                height: '8px',
                                borderRadius: '50%',
                                backgroundColor: '#F55D00',
                                marginTop: '6px'
                              }} />
                              <div>
                                <div style={{ fontWeight: '500', color: '#2A2C41', fontSize: '14px' }}>
                                  {item.description || item.step.replace(/_/g, ' ').replace(/\b\w/g, m => m.toUpperCase())}
                                </div>
                                <div style={{ color: '#6b7280', fontSize: '12px' }}>
                                  {dayjs(item.timestamp).format('MMM DD, YYYY hh:mm A')}
                                </div>
                              </div>
                            </div>
                          ))}
                        </div>
                      </div>
                    </Col>
                  )}

                  {/* Documents */}
                  <Col span={24}>
                    <div style={{
                      backgroundColor: '#F4F4F8',
                      borderRadius: '16px',
                      padding: '20px',
                      border: '1px solid rgba(42, 44, 65, 0.1)'
                    }}>
                      <h4 style={{
                        margin: '0 0 16px 0',
                        fontSize: '16px',
                        fontWeight: '600',
                        color: '#2A2C41',
                        display: 'flex',
                        alignItems: 'center',
                        gap: '8px'
                      }}>
                        <FiFile style={{ fontSize: '18px', color: '#2A2C41' }} />
                        Submitted Documents ({selectedBusiness.documents ? selectedBusiness.documents.length : 0})
                      </h4>
                      <div style={{
                        display: 'grid',
                        gridTemplateColumns: 'repeat(auto-fill, minmax(260px, 1fr))',
                        gap: '12px'
                      }}>
                        {selectedBusiness.documents && selectedBusiness.documents.map((doc, index) => {
                          const docIdentifier = doc.document_id || doc.id || doc.document_type || doc.type || index;
                          const isCurrentlyVerifying = verifyingDocId === docIdentifier;
                          const typeLabel = getDocumentTypeLabel(doc.document_type || doc.type);
                          const fileName = doc.file_name || doc.name || typeLabel;
                          const sizeStr = formatFileSize(doc.file_size);
                          const status = (doc.verification_status || 'pending').toLowerCase();
                          const isDocApproved = status === 'approved' || status === 'verified';
                          const isDocRejected = status === 'rejected';
                          const statusColor = isDocApproved ? '#52c41a' : isDocRejected ? '#ff4d4f' : '#faad14';
                          const statusText = isDocApproved ? 'Approved' : isDocRejected ? 'Rejected' : 'Pending';

                          return (
                            <div
                              key={index}
                              onClick={() => openDocument(doc)}
                              style={{
                                padding: '12px 14px',
                                backgroundColor: isDocApproved ? 'rgba(82, 196, 26, 0.04)' : '#ffffff',
                                borderRadius: '12px',
                                border: isDocApproved ? '1px solid rgba(82, 196, 26, 0.35)' : '1px solid rgba(42, 44, 65, 0.12)',
                                cursor: 'pointer',
                                transition: 'all 0.2s ease',
                                display: 'flex',
                                flexDirection: 'column',
                                gap: '8px'
                              }}
                              onMouseEnter={(e) => {
                                e.currentTarget.style.borderColor = isDocApproved ? '#52c41a' : '#F55D00';
                                e.currentTarget.style.boxShadow = isDocApproved
                                  ? '0 4px 12px rgba(82, 196, 26, 0.15)'
                                  : '0 4px 12px rgba(245, 93, 0, 0.1)';
                              }}
                              onMouseLeave={(e) => {
                                e.currentTarget.style.borderColor = isDocApproved ? 'rgba(82, 196, 26, 0.35)' : 'rgba(42, 44, 65, 0.12)';
                                e.currentTarget.style.boxShadow = 'none';
                              }}
                            >
                              <div style={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between', gap: '8px' }}>
                                <div style={{ display: 'flex', alignItems: 'center', gap: '6px', fontWeight: '600', fontSize: '13px', color: '#2A2C41', overflow: 'hidden' }}>
                                  <FiFileText style={{ color: isDocApproved ? '#52c41a' : '#F55D00', fontSize: '15px', flexShrink: 0 }} />
                                  <span style={{ overflow: 'hidden', textOverflow: 'ellipsis', whiteSpace: 'nowrap' }}>{typeLabel}</span>
                                </div>
                                <Tag color={statusColor} style={{ margin: 0, fontSize: '10px', padding: '0 6px', borderRadius: '4px', lineHeight: '18px' }}>
                                  {statusText}
                                </Tag>
                              </div>
                              <div style={{ fontSize: '11px', color: '#6b7280', overflow: 'hidden', textOverflow: 'ellipsis', whiteSpace: 'nowrap' }} title={fileName}>
                                {fileName}
                              </div>
                              <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', fontSize: '10px', color: '#9ca3af' }}>
                                <span>{sizeStr || 'Document'}</span>
                                <span style={{ color: '#F55D00', fontWeight: '500', display: 'flex', alignItems: 'center', gap: '3px' }}>
                                  Open <FiEye style={{ fontSize: '11px' }} />
                                </span>
                              </div>

                              {/* Provision to Approve each document */}
                              <div
                                style={{
                                  display: 'flex',
                                  alignItems: 'center',
                                  justifyContent: 'flex-end',
                                  gap: '8px',
                                  paddingTop: '8px',
                                  borderTop: '1px solid rgba(42, 44, 65, 0.08)',
                                  marginTop: '2px'
                                }}
                                onClick={(e) => e.stopPropagation()}
                              >
                                {isDocApproved ? (
                                  <Button
                                    size="small"
                                    loading={isCurrentlyVerifying}
                                    onClick={(e) => {
                                      e.stopPropagation();
                                      handleVerifyDocument(doc, 'pending');
                                    }}
                                    style={{
                                      fontSize: '11px',
                                      height: '26px',
                                      borderRadius: '6px',
                                      padding: '0 8px',
                                      color: '#52c41a',
                                      borderColor: '#b7eb8f',
                                      backgroundColor: '#f6ffed',
                                      display: 'inline-flex',
                                      alignItems: 'center',
                                      gap: '4px'
                                    }}
                                  >
                                    <FiCheckCircle style={{ color: '#52c41a' }} /> Approved (Revert)
                                  </Button>
                                ) : (
                                  <Space size={6}>
                                    {!isDocRejected && (
                                      <Button
                                        size="small"
                                        danger
                                        loading={isCurrentlyVerifying}
                                        onClick={(e) => {
                                          e.stopPropagation();
                                          handleVerifyDocument(doc, 'rejected');
                                        }}
                                        style={{
                                          fontSize: '11px',
                                          height: '26px',
                                          borderRadius: '6px',
                                          padding: '0 8px'
                                        }}
                                      >
                                        Reject
                                      </Button>
                                    )}
                                    <Button
                                      size="small"
                                      type="primary"
                                      icon={<FiCheckCircle />}
                                      loading={isCurrentlyVerifying}
                                      onClick={(e) => {
                                        e.stopPropagation();
                                        handleVerifyDocument(doc, 'approved');
                                      }}
                                      style={{
                                        fontSize: '11px',
                                        height: '26px',
                                        borderRadius: '6px',
                                        padding: '0 10px',
                                        backgroundColor: '#52c41a',
                                        borderColor: '#52c41a',
                                        color: '#ffffff',
                                        display: 'inline-flex',
                                        alignItems: 'center',
                                        gap: '4px'
                                      }}
                                    >
                                      Approve
                                    </Button>
                                  </Space>
                                )}
                              </div>
                            </div>
                          );
                        })}
                      </div>
                    </div>
                  </Col>
                </Row>
              </div>

              {/* Modal Actions */}
              <div style={{
                padding: '24px 32px',
                backgroundColor: '#F4F4F8',
                borderTop: '1px solid rgba(42, 44, 65, 0.1)',
                display: 'flex',
                justifyContent: 'space-between',
                alignItems: 'center',
                position: 'sticky',
                bottom: 0,
                zIndex: 10,
                flexShrink: 0
              }}>
                <Button
                  onClick={() => setDetailModalVisible(false)}
                  style={{
                    borderRadius: '12px',
                    borderColor: 'rgba(42, 44, 65, 0.2)',
                    color: '#2A2C41',
                    height: '40px',
                    padding: '0 20px',
                    fontWeight: '500'
                  }}
                >
                  Close
                </Button>
                <Space size={12}>
                  <Button
                    icon={<FiXCircle />}
                    onClick={() => openDecision(selectedBusiness, 'reject')}
                    style={{
                      borderRadius: '12px',
                      backgroundColor: 'rgba(245, 93, 0, 0.1)',
                      borderColor: '#F55D00',
                      color: '#F55D00',
                      height: '40px',
                      padding: '0 20px',
                      fontWeight: '500'
                    }}
                  >
                    Reject
                  </Button>
                  <Button
                    icon={<FiRefreshCw />}
                    onClick={() => openDecision(selectedBusiness, 'pending')}
                    style={{
                      borderRadius: '12px',
                      backgroundColor: '#F4F4F8',
                      borderColor: 'rgba(42, 44, 65, 0.2)',
                      color: '#2A2C41',
                      height: '40px',
                      padding: '0 20px',
                      fontWeight: '500'
                    }}
                  >
                    Request Changes
                  </Button>
                  {/* Only show Approve button if business is NOT approved */}
                  {(() => {
                    const status = selectedBusiness?.status;
                    const rawStatus = selectedBusiness?.rawStatus;
                    const isApproved =
                      status === 'Approved' ||
                      status === 'approved' ||
                      rawStatus === 'approved' ||
                      rawStatus === 'Approved' ||
                      (typeof status === 'string' && status.toLowerCase() === 'approved') ||
                      (typeof rawStatus === 'string' && rawStatus.toLowerCase() === 'approved');

                    if (isApproved) {
                      return null; // Don't show Approve button for approved businesses
                    }

                    return (
                      <Button
                        type="primary"
                        icon={<FiCheckCircle />}
                        onClick={() => openDecision(selectedBusiness, 'approve')}
                        style={{
                          borderRadius: '12px',
                          backgroundColor: '#FDBF50',
                          borderColor: '#FDBF50',
                          height: '40px',
                          padding: '0 20px',
                          fontWeight: '500',
                          boxShadow: '0 4px 12px rgba(253, 191, 80, 0.3)'
                        }}
                      >
                        Approve
                      </Button>
                    );
                  })()}
                </Space>
              </div>
            </div>
          )}
        </Modal>
        {/* Decision Modal */}
        <Modal
          title={decisionAction === 'approve' ? 'Approve Business' : decisionAction === 'reject' ? 'Reject Business' : 'Request Changes'}
          open={decisionModalVisible}
          onCancel={() => setDecisionModalVisible(false)}
          onOk={async () => { await submitDecision(); setDetailModalVisible(false); }}
          okText={decisionAction === 'approve' ? 'Approve' : decisionAction === 'reject' ? 'Reject' : 'Save'}
          className={decisionAction === 'approve' ? 'approve-modal-modern' : decisionAction === 'reject' ? 'reject-modal-modern' : 'pending-modal-modern'}
          width={600}
        >
          <div className="reject-form-section">
            <label className="reject-form-label optional">Comments (optional)</label>
            <textarea
              className="reject-textarea"
              rows={3}
              value={decisionComments}
              onChange={(e) => setDecisionComments(e.target.value)}
              placeholder="Add comments for the owner..."
            />
          </div>

          {decisionAction === 'reject' && (
            <div>
              <label className="reject-form-label">Rejection Reasons</label>
              <Select
                mode="multiple"
                className="reject-select"
                style={{ width: '100%', marginBottom: '8px' }}
                placeholder="Select reasons from templates"
                loading={templatesLoading}
                value={decisionRejectionReasons.filter(r => r.template_id).map(r => r.template_id)}
                onChange={(selectedIds) => {
                  const templateReasons = selectedIds.map(id => {
                    const template = reviewTemplates.rejection.find(t => t.id === id);
                    return {
                      template_id: id,
                      title: template?.title || '',
                      description: template?.description || ''
                    };
                  });
                  const customReasons = decisionRejectionReasons.filter(r => !r.template_id);
                  setDecisionRejectionReasons([...templateReasons, ...customReasons]);
                }}
              >
                {reviewTemplates.rejection.map(template => (
                  <Option key={template.id} value={template.id}>
                    <div>
                      <div style={{ fontWeight: '500' }}>{template.title}</div>
                      <div style={{ fontSize: '12px', color: '#666' }}>
                        {template.category} - {template.description}
                      </div>
                    </div>
                  </Option>
                ))}
              </Select>

              <Select
                mode="tags"
                className="reject-select"
                style={{ width: '100%' }}
                placeholder="Add custom reasons..."
                value={decisionRejectionReasons.filter(r => !r.template_id).map(r => r.title)}
                onChange={(customTitles) => {
                  const templateReasons = decisionRejectionReasons.filter(r => r.template_id);
                  const customReasons = customTitles.map(title => ({
                    title,
                    description: ''
                  }));
                  setDecisionRejectionReasons([...templateReasons, ...customReasons]);
                }}
              />
            </div>
          )}

          {decisionAction === 'pending' && (
            <div style={{ padding: '0 10px' }}>
              <label className="reject-form-label">Required Changes</label>
              <Select
                mode="multiple"
                className="reject-select"
                style={{ width: '100%', marginBottom: '8px' }}
                placeholder="Select changes from templates"
                loading={templatesLoading}
                value={decisionRequiredChanges.filter(r => r.template_id).map(r => r.template_id)}
                onChange={(selectedIds) => {
                  const templateChanges = selectedIds.map(id => {
                    const template = reviewTemplates.required_changes.find(t => t.id === id);
                    return {
                      template_id: id,
                      title: template?.title || '',
                      description: template?.description || ''
                    };
                  });
                  const customChanges = decisionRequiredChanges.filter(r => !r.template_id);
                  setDecisionRequiredChanges([...templateChanges, ...customChanges]);
                }}
              >
                {reviewTemplates.required_changes.map(template => (
                  <Option key={template.id} value={template.id}>
                    <div>
                      <div style={{ fontWeight: '500' }}>{template.title}</div>
                      <div style={{ fontSize: '12px', color: '#666' }}>
                        {template.category} - {template.description}
                      </div>
                    </div>
                  </Option>
                ))}
              </Select>

              <Select
                mode="tags"
                className="reject-select"
                style={{ width: '100%' }}
                placeholder="Add custom changes..."
                value={decisionRequiredChanges.filter(r => !r.template_id).map(r => r.title)}
                onChange={(customTitles) => {
                  const templateChanges = decisionRequiredChanges.filter(r => r.template_id);
                  const customChanges = customTitles.map(title => ({
                    title,
                    description: ''
                  }));
                  setDecisionRequiredChanges([...templateChanges, ...customChanges]);
                }}
              />
            </div>
          )}
        </Modal>

      </div>
    </div>
  );
};

export default PendingBusinessDashboard;